import { generateClient } from "aws-amplify/api";
import { fetchUserAttributes, getCurrentUser } from "@aws-amplify/auth";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";

/* Plan loading effects intentionally control their own fetch lifecycle. */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  createCreatePlan,
  createNotifications,
  createUserPayments,
  updateCoupon,
  updateCreatePlan,
  updateUserPayments,
} from "../graphql/mutations.js";
import {
  getCreatePlan,
  getCoupon,
  listCreatePlans,
  listOrganizationCPs,
  listUserLinks,
  listUserPayments,
} from "../graphql/queries.js";
// PayPal components
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";

const PAYPAL_CLIENT_ID =
  "AbTmKuryR63tH6ernrjPWq4bD-bJCwzObx8HPzxH-vDjeXNF8KEZwZC4_kokki9kiwU4UQRTGjcBzmDk";

/* Use the default generated client (consistent with other working scripts) */
// The file declares `client = generateClient()` further down and we'll use that.

const strengthOptions = [
  {
    label: "Problem-Solving Skills",
    options: [
      { value: "PSProblemSolving", label: "Problem-Solving Skills" },
      { value: "PSSelfEsteem", label: "Self-Esteem and Confidence" },
      { value: "PSChoiceAutonomy", label: "Choice Autonomy" },
      { value: "PSSelfCare", label: "Self-Care" },
      { value: "PSSelfAwareness", label: "Self-Awareness" },
      { value: "PSCreativity", label: "Creativity" },
    ],
  },
  {
    label: "Character Strengths",
    options: [
      { value: "CSForgiveness", label: "Forgiveness" },
      { value: "CSKindness", label: "Kindness" },
      { value: "CSGratitude", label: "Gratitude" },
      { value: "CSPatience", label: "Patience" },
      { value: "CSCourage", label: "Courage" },
    ],
  },
  {
    label: "Coping Strengths",
    options: [
      { value: "CMSCopingChoice", label: "Coping Mechanisms" },
      { value: "CMSResilience", label: "Resilience and Perseverance" },
      { value: "CMSSelfRegulation", label: "Self-Regulation" },
      { value: "CMSOptimism", label: "Hope and Optimism" },
    ],
  },
  {
    label: "Community Strengths",
    options: [
      { value: "COMSupportSystem", label: "Strong Support System" },
      { value: "COMSocialIntelligence", label: "Social Intelligence" },
      { value: "COMCommunication", label: "Communication Skills" },
      { value: "COMFaithCommunity", label: "Faith Based Community" },
      { value: "COMAccomplishments", label: "Accomplishments" },
    ],
  },
];

const barrierOptions = [
  {
    label: "Barriers",
    options: [
      { value: "BRStableHousing", label: "Access to Safe Stable Housing" },
      { value: "BRQualityHealthcare", label: "Access to Quality Healthcare" },
      {
        value: "BRProfessionalAccess",
        label: "Access to Clinical Professionals",
      },
      { value: "BRInsuranceCoverage", label: "Insurance Coverage" },
      { value: "BRFinancialConstraints", label: "Financial Constraints" },
      { value: "BREducationAttainment", label: "Education Attainment" },
      { value: "BRSocialStigma", label: "Social Stigma and Misconceptions" },
      {
        value: "BRUnsupportiveCommunity",
        label: "Unsupportive Personal Community",
      },
      {
        value: "BRNonexistentCommunity",
        label: "Nonexistent Personal Community",
      },
      {
        value: "BRCommunityResources",
        label: "Awareness of Community Resources",
      },
      {
        value: "BRProfessionalTrust",
        label: "Personal Trust in Clinical Professionals",
      },
      {
        value: "BRHealthcareSystemTrust",
        label: "Personal Trust in Healthcare System",
      },
      {
        value: "BRCognitiveImpairments",
        label: "Cognitive Impairments (e.g., memory, concentration)",
      },
      { value: "BRDependentCare", label: "Child / Elder Care" },
      { value: "BRTransportation", label: "Transportation" },
      { value: "BRTechnologyAccess", label: "Access to Technology" },
      { value: "BRLanguage", label: "Language or Communication" },
      {
        value: "BRHealthcareProcess",
        label: "Navigating Healthcare Processes",
      },
      { value: "BROther", label: "Other" }, // Note your schema uses BROther for 'Other' barrier
    ],
  },
];

const noteTriggersOptions = [
  {
    label: "Note Triggers",
    options: [
      { value: "TGBetrayal", label: "Betrayal" },
      { value: "TGBoundaries", label: "Ignored Boundaries" },
      { value: "TGDisorder", label: "Loud Noise / Disorder / Chaos" },
      { value: "TGPain", label: "Pain (Emotional or Physical)" },
      { value: "TGRejection", label: "Rejection" },
      { value: "TGStress", label: "Stressful Life Events" },
      { value: "TGMemories", label: "Traumatic Memories" },
      { value: "TGUnjustTreatment", label: "Unjust Treatment" },
      {
        value: "TGUnpleasantConversations",
        label: "Unpleasant Conversations (e.g. yelling, criticism, etc.)",
      },
      { value: "TGUnsafeEnvironments", label: "Unsafe Environments" },
      { value: "TGOther", label: "Other" },
    ],
  },
];

const noteWarningSignsOptions = [
  {
    label: "Note Warning Signs",
    options: [
      { value: "WSFlashbacks", label: "Flashbacks" },
      { value: "WSIncreasedAppetite", label: "Increased Appetite" },
      { value: "WSDecreasedAppetite", label: "Decreased Appetite" },
      { value: "WSIntenseEmotions", label: "Intense Emotions / Irritability" },
      { value: "WSDisconnecting", label: "Isolation / Disconnecting" },
      { value: "WSTime", label: "Losing Track of Time" },
      { value: "WSNegativeSelfTalk", label: "Negative Self Talk" },
      { value: "WSNeglectingSelfCare", label: "Neglecting Self Care" },
      { value: "WSNightmares", label: "Nightmares" },
      { value: "WSRecklessBehavior", label: "Reckless / Destructive Behavior" },
      { value: "WSOther", label: "Other" },
    ],
  },
];
// 🔹 Lab ranges for flagging in Create Plan
const labRanges = {
  // Lipids
  LabsTG: { type: "lt", max: 150 },
  LabsTC: { type: "lt", max: 200 },
  LabsHDL: { type: "gt", min: 40 },
  LabsLDL: { type: "lt", max: 100 },

  // Minerals
  LabsNa: { type: "between", min: 134, max: 144 },
  LabsK: { type: "between", min: 3.5, max: 5.2 },
  LabsP: { type: "between", min: 2.5, max: 4.5 },
  LabsCl: { type: "between", min: 96, max: 106 },
  LabsZn: { type: "between", min: 44, max: 115 },
  LabsCa: { type: "between", min: 8.7, max: 10.2 },
  LabsMg: { type: "between", min: 1.6, max: 2.3 },
  LabsFe: { type: "between", min: 149, max: 505 },
  LabsFerritin: { type: "between", min: 11, max: 336 },

  // Vitamins
  LabsVitA: { type: "between", min: 20.1, max: 62 },
  LabsVitB1: { type: "between", min: 2.5, max: 7.5 },
  LabsVitB2: { type: "between", min: 106, max: 638 },
  LabsVitB3: { type: "between", min: 0.5, max: 8.45 },
  LabsVitB5: { type: "between", min: 1.6, max: 2.7 },
  LabsVitB6: { type: "between", min: 5, max: 50 },
  LabsVitB7: { type: "between", min: 133, max: 329 },
  LabsVitB9: { type: "gt_or_eq", min: 3 },
  LabsVitB12: { type: "between", min: 232, max: 1245 },
  LabsVitC: { type: "between", min: 0.4, max: 2 },
  LabsVitD: { type: "between", min: 30, max: 100 },
  LabsVitE: { type: "between", min: 7, max: 25.1 },
  LabsVitK: { type: "between", min: 0.22, max: 4.88 },
};

const lipidLabKeys = ["LabsTG", "LabsTC", "LabsHDL", "LabsLDL"];
const mineralLabKeys = [
  "LabsNa",
  "LabsK",
  "LabsP",
  "LabsCl",
  "LabsZn",
  "LabsCa",
  "LabsMg",
  "LabsFe",
  "LabsFerritin",
];

const vitaminLabKeys = [
  "LabsVitA",
  "LabsVitB1",
  "LabsVitB2",
  "LabsVitB3",
  "LabsVitB5",
  "LabsVitB6",
  "LabsVitB7",
  "LabsVitB9",
  "LabsVitB12",
  "LabsVitC",
  "LabsVitD",
  "LabsVitE",
  "LabsVitK",
];
const allLabKeys = [...lipidLabKeys, ...mineralLabKeys, ...vitaminLabKeys];
const assessmentScorePrefixes = ["TR", "CM", "MH", "PH", "SH", "SoH"];

function isAssessmentScoreActionKey(key) {
  if (key.startsWith("SDoH")) return true;
  return assessmentScorePrefixes.some((prefix) => key.startsWith(prefix));
}

function isLabValueOutOfRange(fieldKey, rawValue) {
  if (rawValue === null || rawValue === undefined || rawValue === "")
    return false;
  const cfg = labRanges[fieldKey];
  if (!cfg) return false;

  const value = parseFloat(rawValue);
  if (Number.isNaN(value)) return false;

  const { type, min, max } = cfg;

  switch (type) {
    case "lt":
      return !(value < max);
    case "gt":
      return !(value > min);
    case "gt_or_eq":
      return !(value >= min);
    case "between":
      return !(value >= min && value <= max);
    default:
      return false;
  }
}

const Actions = {
  // 🔹 LABS — each has the same default action for now
  LabsTG: [
    "Increase Whole Food Meals",
    "Increase Aerobic Exercise",
    "Increase Strength Training",
    "Sleep 7-9 Hours Per Day",
    "Avoid Added Sugars",
    "Avoid Refined Carbohydrates (White bread, rice cakes, etc.)",
    "Avoid Excessive Alcohol ",
    "Replace Sugary Drinks With Water ",
    "Other (when chosen provide free text)",
  ],

  LabsTC: [
    "Increase Dietary Fiber (Lentils, beans, seeds, fruit, etc.)",
    "Increase Whole Plant-Based Meals",
    "Exercise 20+ Minutes Per Day",
    "Avoid Saturated Fats (Butter, cheese, bacon, coconut oil, etc.)",
    "Avoid Added Sugars",
    "Address Chronic Stress ",
    "Other (When Chosen Provide Free Text)",
  ],

  LabsHDL: [
    "Exercise 20+ Minutes Per Day",
    "Increase Aerobic Exercise",
    "Increase Resistance Training",
    "Sleep 7-9 Hours Per Day",
    "Stop Smoking (Complete smoking cessation program, etc.)",
    "Avoid Refined Carbohydrates (White bread, rice cakes, etc.)",
    "Avoid Excessive Alcohol",
    "Other (When Chosen Provide Free Text)",
  ],

  LabsLDL: [
    "Increase Soluble Fiber (Legumes, Oats, Flax, etc.)",
    "Exercise 20+ Minutes Per Day",
    "Sleep 7-9 Hours Per Day",
    "Stop Smoking (Complete smoking cessation program, etc.)",
    "Avoid Refined Carbohydrates (White bread, rice cakes, etc.)",
    "Avoid Saturated Fats (Butter, cheese, bacon, coconut oil, etc.)",
    "Eliminate Trans Fats (Foods containing partially hydrogenated oils)",
    "Other (When Chosen Provide Free Text)",
  ],

  LabsNa: [
    "Increase Water Consumption",
    "Use Mineral Salt (Sea salt, etc.)",
    "Increase Exercise (Sweating can improve salt levels in the body)",
    "Address Potassium if Needed",
    "Avoid Excessive Sugar",
    "Avoid Processed Foods",
    "Other (when chosen provide free text)",
  ],

  LabsK: [
    "Increase Potassium-Rich Foods (Spinach, beans, avocado, etc.)",
    "Increase Whole, Minimally Processed Foods",
    "Increase Water Intake",
    "Reduce Excess Sodium Intake",
    "Reduce Caffeine",
    "Address Gut Issues (Deficiency could be caused by lack of absorption)",
    "Address Diuretics Or Laxative (Consult a physician)",
    "Address Magnesium if Needed",
    "Address Chronic Stress (Cortisol impacts electrolytes)",
    "Other (When Chosen Provide Free Text)",
  ],

  LabsP: [
    "Increase Protein Intake (Lentils, beans, seeds, etc.)",
    "Address Vitamin D if Needed",
    "Address Gut Issues (Deficiency could be caused by lack of absorption)",
    "Avoid Excessive Antacid Use (If medically appropriate)",
    "Avoid Excessive Alcohol ",
    "Other (When Chosen Provide Free Text)",
  ],

  LabsCl: [
    "Increase Mineral-Rich Broths",
    "Increase Sodium Intake (Chloride follows sodium)",
    "Address Diuretics Or Laxative (Consult a physician)",
    "Consider Oral Electrolytes",
    "Other (When Chosen Provide Free Text)",
  ],

  LabsZn: [
    "Increase Seeds and Nuts (Pumpkin seeds, etc.)",
    "Soak Grains/Legumes (Reduces phytates which can inhibit absorption)",
    "Avoid Excessive Alcohol",
    "Address Copper Intake in Needed",
    "Address Gut Issues (Deficiency could be caused by lack of absorption)",
    "Other (when chosen provide free text)",
  ],

  LabsCa: [
    "Increase Low Oxalate Leafy Greens (Oxalates can inhibit absorption)",
    "Increase Foods Fortified with Calcium",
    "Address Vitamin D if Needed",
    "Address Vitamin K2 if Needed",
    "Address Magnesium if Needed",
    "Avoid Excessive Soda",
    "Increase Strength Training Exercise",
    "Take Supplements",
    "Other (when chosen provide free text)",
  ],

  LabsMg: [
    "Increase Seeds and Nuts (Pumpkin seeds, etc.)",
    "Increase Leafy Greens",
    "Increase Lentils and Beans",
    "Reduce Chronic Stress",
    "Avoid Excessive Alcohol",
    "Avoid Ultra-Processed Foods",
    "Address Vitamin D if Needed",
    "Take Supplements",
    "Other (when chosen provide free text)",
  ],

  LabsFe: [
    "Increase Seeds and Nuts",
    "Increase Leafy Greens",
    "Avoid Tea/Coffee with Meals",
    "Address Copper if Needed",
    "Address Vitamin C if Needed",
    "Address Gut Issues (Deficiency could be caused by lack of absorption)",
    "Take Supplements (Prioritize correction through diet, take calcium and iron at different times)",
    "Other (when chosen provide free text)",
  ],

  LabsFerritin: [
    "Increase Seeds and Nuts",
    "Increase Leafy Greens",
    "Avoid Tea/Coffee with Meals",
    "Address Copper if Needed",
    "Address Vitamin C if Needed",
    "Address Gut Issues (Deficiency could be caused by lack of absorption)",
    "Take Supplements (Prioritize correction through diet, take calcium and iron at different times)",
    "Other (when chosen provide free text)",
  ],

  LabsVitA: [
    "Increase Orange Veggies (Carrots, sweet potato, pumpkin, etc.)",
    "Increase Dark Leafy Greens (Spinach, kale, Brussels sprouts, etc.)",
    "Pair Plant Sources with Healthy Fat (Olive oil, avocado, coconut oil, etc.)",
    "Avoid Chronic Very Low-Fat Diets (Vitamin A dissolves in fat)",
    "Avoid Excessive Alcohol (Impairs storage of vitamin A)",
    "Take Supplements (See a physician to confirm amount as there is a risk of toxicity)",
    "Other (when chosen provide free text)",
  ],

  LabsVitB1: [
    "Increase Seeds and Nuts (Sunflower seeds, etc.)",
    "Increase Whole Grains (Not refined and ideally organic to avoid glyphosate)",
    "Increase Beans and Legumes (Lentils, Black beans, etc.)",
    "Avoid Excessive Alcohol",
    "Avoid White Rice/Flour",
    "Take Supplements",
    "Other (when chosen provide free text)",
  ],

  LabsVitB2: [
    "Increase Seeds and Nuts (Almonds, etc.)",
    "Increase Consumption of Foods Fortified with B Vitamins",
    "Avoid Chronic Calorie Restriction",
    "Address B if Needed",
    "Avoid Excessive Caffeine",
    "Take Supplements",
    "Other (when chosen provide free text)",
  ],

  LabsVitB3: [
    "Increase Seeds and Nuts (Peanuts, etc.)",
    "Increase Whole Grains (Not refined and ideally organic to avoid glyphosate)",
    "Increase Protein (The body converts excess tryptophan into niacin)",
    "Avoid Excessive Alcohol",
    "Avoid Ultra-Refined Carbs",
    "Take Supplements (See a physician as there is a risk of flushing)",
    "Other (when chosen provide free text)",
  ],

  LabsVitB5: [
    "Increase Avocado (Offers a nutrient dense healthy fat)",
    "Increase Vegetables (Offer fiber and antioxidant properties)",
    "Increase Whole Grains (Not refined and ideally organic to avoid glyphosate)",
    "Avoid Over-Processing Foods",
    "Reduce Chronic Stress (Stress increases the demand for B5)",
    "Take Supplements",
    "Other (when chosen provide free text)",
  ],

  LabsVitB6: [
    "Increase Bananas",
    "Increase Potatoes",
    "Increase Chickpeas",
    "Avoid Excessive Alcohol",
    "Address Magnesium if Needed",
    "Take Supplements (Avoid long-term very high-dose supplements)",
    "Other (when chosen provide free text)",
  ],

  LabsVitB7: [
    "Increase Seeds And Nuts",
    "Increase Sweet Potatoes",
    "Address Gut Issues (Deficiency could be caused by lack of absorption)",
    "Avoid Excessive Antibiotics",
    "Avoid Excessive Alcohol",
    "Reduce Ultra-Processed Foods",
    "Take Supplements (See a physician as there is a risk of it interfering with labs)",
    "Other (when chosen provide free text)",
  ],

  LabsVitB9: [
    "Increase Dark Leafy Greens",
    "Increase Lentils and Beans",
    "Increase Asparagus",
    "Increase Avocado",
    "Increase Citrus Fruits",
    "Avoid Excessive Alcohol",
    "Take Supplements (Prioritize correction in diet if possible)",
    "Other (when chosen provide free text)",
  ],

  LabsVitB12: [
    "Avoid Chronic Antacid Use if Possible",
    "Address Gut Issues (Research suggests that B12 is made by bacteria in the gut)",
    "Address Iron if Needed",
    "Take Supplements",
    "Other (when chosen provide free text)",
  ],

  LabsVitC: [
    "Increase Citrus Fruits",
    "Increase Bell Peppers",
    "Increase Kiwi",
    "Increase Strawberries",
    "Prioritize Fresh Fruits and Vegetables (Not stored for long)",
    "Avoid Smoking",
    "Take Supplements",
    "Other (when chosen provide free text)",
  ],

  LabsVitD: [
    "Increase Sun Exposure",
    "Address K2 if Needed",
    "Take Supplements (Research indicates up to 4000 UI per day. See physician to determine appropriate amount.)",
    "Other (when chosen provide free text)",
  ],

  LabsVitE: [
    "Increase Seeds and Nuts (Almonds, Sunflower seeds, etc.)",
    "Increase Healthy Fats (Olive oil, avocado, etc.)",
    "Prioritize Whole Foods (Avoid processed foods)",
    "Avoid Ultra-Low-Fat Diets",
    "Address Omega-6 if Needed",
    "Address Selenium if Needed",
    "Take Supplements (See a physician to avoid over supplementation)",
    "Other (when chosen provide free text)",
  ],

  LabsVitK: [
    "Increase Leafy Greens",
    "Avoid Long-Term Antibiotics",
    "Address Vitamin D if Needed",
    "Address Calcium if Needed",
    "Address Gut Issues (Deficiency could be caused by lack of absorption)",
    "Take Supplements",
    "Other (when chosen provide free text)",
  ],

  CMAlcohol: [
    "Call 988 When Triggered (No Frequency)",
    "Avoid High Risk Environments (No Frequency)",
    "Engage in Meaningful Work, Education, or Volunteerism",
    "Engage in Professional Individual Therapy",
    "Complete a Support Group / Program (when chosen provide free text)",
    "Engage in a Positive Coping Mechanism (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  CMDrugs: [
    "Call 988 When Triggered (No Frequency)",
    "Avoid High Risk Environments / Connections (No Frequency)",
    "Engage in Meaningful Work, Education, or Volunteerism",
    "Engage in Professional Individual Therapy",
    "Complete a Support Group / Program (when chosen provide free text)",
    "Engage in a Positive Coping Mechanism (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  CMOvereating: [
    "Consciously Eat – Chew Thoroughly Focusing on Taste, Smell, and Texture",
    "Practice Deep Breathing for Stress Management (Stimulates the parasympathetic nervous system-rest/relaxation)",
    "Practice Stretching for Stress Management (Stimulates the parasympathetic nervous system-rest/relaxation)",
    "Focus on Whole, Unprocessed, Home Cooked Foods",
    "Increase Fiber Rich Foods (e.g. Vegetables, Beans, etc.) (Promote fullness and satiety and supports gut health)",
    "Choose Water Over Sugary Drinks and/or Alcohol",
    "Keep a Food Journal to Identify Trends and Triggers",
    "Prep Environment - Smaller Dishware, Storing Food Out of Sight",
    "Engage in a Positive Coping Mechanism (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  CMSmoking: [
    "Avoid High Risk Environments / Connections (No Frequency)",
    "Practice Deep Breathing for Stress Management (Stimulates the parasympathetic nervous system-rest/relaxation)",
    "Practice Stretching for Stress Management (Stimulates the parasympathetic nervous system-rest/relaxation)",
    "Engage in Professional Individual Therapy",
    "Complete a Support Group / Program (when chosen provide free text)",
    "Engage in a Positive Coping Mechanism (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  CMGaming: [
    "Establish Time Limits (No Frequency)",
    "Remove Devices that Provide Access (No Frequency)",
    "Engage in Meaningful Work, Education, or Volunteerism",
    "Engage in Professional Individual Therapy",
    "Complete a Support Group / Program (when chosen provide free text)",
    "Engage in a Positive Coping Mechanism (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  CMShopping: [
    "Avoid High Risk Environments / Connections (No Frequency)",
    "Practice Deep Breathing for Stress Management (Stimulates the parasympathetic nervous system-rest/relaxation)",
    "Practice Stretching for Stress Management (Stimulates the parasympathetic nervous system-rest/relaxation)",
    "Keep a Journal to Identify Trends and Triggers",
    "Complete a Support Group / Program (when chosen provide free text)",
    "Engage in a Positive Coping Mechanism (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  CMGambling: [
    "Remove Devices that Provide Access (No Frequency)",
    "Avoid High Risk Environments / Connections (No Frequency)",
    "Engage in Professional Individual Therapy",
    "Engage in Professional Family Therapy",
    "Complete a Support Group / Program (when chosen provide free text)",
    "Engage in a Positive Coping Mechanism (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  CMSex: [
    "Call 988 When Triggered (No Frequency)",
    "Avoid High Risk Environments / Connections (No Frequency)",
    "Engage in Meaningful Work, Education, or Volunteerism",
    "Engage in Professional Individual Therapy",
    "Complete a Support Group / Program (when chosen provide free text)",
    "Engage in a Positive Coping Mechanism (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  CMPorn: [
    "Call 988 When Triggered (No Frequency)",
    "Remove Devices that Provide Access (No Frequency)",
    "Engage in Professional Individual Therapy",
    "Engage in Professional Family Therapy",
    "Complete a Support Group / Program (when chosen provide free text)",
    "Engage in a Positive Coping Mechanism (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  CMBlaming: [
    "Examine and Correct Beliefs / Thoughts / Words About Self (No Frequency)",
    "Practice Enforcing Healthy Boundaries (No Frequency)",
    "Practice Communicating Mistakes as Opportunities (No Frequency)",
    "Engage in Professional Individual Therapy",
    "Engage in Professional Family Therapy",
    "Complete a Self-Development Book / Program (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  CMHurting: [
    "Call 988 When Triggered (No Frequency)",
    "Avoid High Risk Environments / Connections (No Frequency)",
    "Engage in Professional Individual Therapy",
    "Engage in Professional Family Therapy",
    "Complete a Support Group / Program (when chosen provide free text)",
    "Engage in a Positive Coping Mechanism (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  CMDisengage: [
    "Practice Enforcing Healthy Boundaries (No Frequency)",
    "Practice Having Challenging Conversations / Problem Solving (No Frequency)",
    "Practice Forgiveness and / or Conflict Resolution (No Frequency)",
    "Engage in Professional Individual Therapy",
    "Engage in Professional Family Therapy",
    "Complete a Book / Program on Healthy Relationships/Communication (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  MHSleeplessness: [
    "Use the Bed for Common Bed Activities Only (No Frequency)",
    "Challenge Unhelpful Beliefs About Sleep (No Frequency)",
    "Keep a Consistent Sleep-Wake Schedule",
    "Create a Dark and Quiet Sleep Environment",
    "No Caffeine, Nicotine, and/or Alcohol 5 Hours Before Bed",
    "No Screen Time 1 Hour Before Bed",
    "No Meals, Fluids, or Exercise 2 Hours Before Bed",
    "Play Soft, Peaceful, Instrumental Music While Sleeping",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  MHAlcoholUse: [
    "Avoid High Risk Environments (No Frequency)",
    "Engage in Meaningful Work, Education, or Volunteerism",
    "Engage in Professional Individual Therapy",
    "Engage in Professional Family Therapy",
    "Complete a Support Group / Program (when chosen provide free text)",
    "Engage in a Positive Coping Mechanism (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  MHAnxiety: [
    "Actively Correct Anxious Thoughts (No Frequency)",
    "Increase Aerobic Exercise (Boosts mood and decreases stress hormones)",
    "Increase Anti-Inflammatory and Nutrient-Rich Foods",
    "Reduce Caffeine, Sugar, and/or Alcohol",
    "Practice Breathing and Relaxation Techniques (Stimulates the parasympathetic nervous system-rest/relaxation)",
    "Journal or Practice Expressive Writing",
    "Improve Sleep Quality (when chosen provide free text)",
    "Complete a Support Group / Program (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  MHDepression: [
    "Increase Aerobic Exercise (Boosts mood and decreases stress hormones)",
    "Increase Anti-Inflammatory and Nutrient-Rich Foods",
    "Reduce Processed Food and Sugar",
    "Increase Engagement in Meaningful and Pleasurable Activities",
    "Address Relationship Issues, Grief, or Life Transitions",
    "Journal or Practice Expressive Writing",
    "Improve Sleep Quality (when chosen provide free text)",
    "Complete a Support Group / Program (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  MHDrugUse: [
    "Avoid High Risk Environments (No Frequency)",
    "Engage in Meaningful Work, Education, or Volunteerism",
    "Keep a Journal to Identify Trends and Triggers",
    "Engage in Professional Individual Therapy",
    "Complete a Support Group / Program (when chosen provide free text)",
    "Engage in a Positive Coping Mechanism (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  MHGrief: [
    "Work on a Legacy Project (No Frequency)",
    "Engage in Meaningful Work, Education, or Volunteerism",
    "Journal or Practice Expressive Writing",
    "Engage in Professional Individual Therapy",
    "Complete a Support Group / Program (when chosen provide free text)",
    "Engage in a Positive Coping Mechanism (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  MHGuilt: [
    "Reframe Guilt in Terms of Growth and Learning (No Frequency)",
    "Verbalize or Internalize Forgiving Yourself (No Frequency)",
    "Externally Apologize and Ask for Forgiveness (No Frequency)",
    "Journal or Practice Expressive Writing",
    "Engage in Professional Individual Therapy",
    "Engage in Professional Family Therapy",
    "Complete a Support Group / Program (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  MHIrritability: [
    "Practice Enforcing Healthy Boundaries (No Frequency)",
    "Practice Healthy Assertive Communication (No Frequency)",
    "Increase Aerobic Exercise (Regulates cortisol and boosts serotonin)",
    "Increase Anti-Inflammatory and Nutrient-Rich Foods",
    "Reduce Caffeine, Sugar, and/or Alcohol",
    "Journal or Practice Expressive Writing",
    "Engage in Professional Individual Therapy",
    "Improve Sleep Quality (when chosen provide free text)",
    "Engage in a Positive Coping Mechanism (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  MHStress: [
    "De-Clutter Living Space",
    "Practice Gratefulness",
    "Laugh / Watch Comedy",
    "Spend Time in Nature (Stress management)",
    "Engage in Professional Individual Therapy",
    "Engage in Professional Family Therapy",
    "Engage in a Positive Coping Mechanism (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  MHRegret: [
    "Reframe Thoughts of Regret as a Positive Call to Action (No Frequency)",
    "Practice Self Compassion and Forgive Yourself (No Frequency)",
    "Make Amends / Apologize / Remedy (No Frequency)",
    "Practice Gratefulness",
    "Journal or Practice Expressive Writing",
    "Engage in Professional Individual Therapy",
    "Engage in Professional Family Therapy",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  MHSuicidalThoughts: [
    "Call 988 When Triggered (No Frequency)",
    "Examine and Correct Beliefs / Thoughts / Words About Self (No Frequency)",
    "Practice Granular Emotions",
    "Engage in Meaningful Work, Education, or Volunteerism",
    "Engage in Professional Individual Therapy",
    "Complete a Support Group / Program (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  MHLoneliness: [
    "Engage in Self Reflection / Self Discovery (No Frequency)",
    "Spend Time in Nature and Outdoors",
    "Participate in a Club or Interest Group",
    "Engage in Meaningful Work, Education, or Volunteerism",
    "Engage in Professional Individual Therapy",
    "Nurture Existing Relationship(s) (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  MHWorry: [
    "Actively Correct Worrisome Thoughts (No Frequency)",
    "Practice Gratefulness",
    "Spend Time in Nature (Stress management)",
    "Journal or Practice Expressive Writing",
    "Engage in Professional Individual Therapy",
    "Engage in a Positive Coping Mechanism (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],
  PHVegeFruits: [
    "Create and Follow Weekly Menu",
    "Eat at Least One Serving of Vegetables",
    "Eat at Least One Serving of Fruit",
    "Remove Processed Food from Home",
    "Prepare Cooked Meals in Advance",
    "Eat Home Cooked Meal for Lunch",
    "Other (when chosen provide free text)",
  ],

  PHBeanLentils: [
    "Create and Follow Weekly Menu",
    "Eat at Least One Serving of Legumes / Beans",
    "Prepare Cooked Meals in Advance",
    "Eat Home Cooked Meal for Lunch",
    "Other (when chosen provide free text)",
  ],

  PHMeat: [
    "Create and Follow Weekly Menu",
    "Eat a Meatless Meal",
    "Prepare Cooked Meals in Advance",
    "Eat Home Cooked Meal for Lunch",
    "Other (when chosen provide free text)",
  ],

  PHFishSeafood: [
    "Create and Follow Weekly Menu",
    "Eat a Meal without Fish / Seafood",
    "Prepare Cooked Meals in Advance",
    "Eat Home Cooked Meal for Lunch",
    "Other (when chosen provide free text)",
  ],

  PHSweets: [
    "Create and Follow Weekly Menu",
    "Remove Simple Sugars from Meals / Snacks",
    "Remove Sugary Drinks and Snacks from Home",
    "Other (when chosen provide free text)",
  ],

  PHWater: [
    "Drink at Least Four Cups of Water",
    "Remove Sugary Drinks",
    "Other (when chosen provide free text)",
  ],

  PHPhysicalActivity: [
    "At Least 30 Minutes of Cardio Exercise",
    "At Least 30 Reps for Weight Lifting",
    "At Least 30 Minutes of Stretching",
    "At Least 30 Minutes of Walking / Jogging / Running",
    "Other (when chosen provide free text)",
  ],

  SHSpiritualDefine: [
    "Pray",
    "Complete a Fast",
    "At Least 10 Minutes of Reading Religious Text",
    "Engage in Personal Devotion",
    "Other (when chosen provide free text)",
  ],

  SHSpiritualIntegrate: [
    "Pray",
    "Complete a Fast",
    "Read a Faith Based Book",
    "Participate in a Ministry",
    "Participate in a Small Group",
    "Participate in Formal Gatherings",
    "Listen to / Sing Worship Music",
    "Share Personal Testimony",
    "Engage in Personal Devotion",
    "Other (when chosen provide free text)",
  ],

  SHPrayer: [
    "Pray",
    "Complete a Fast",
    "At Least 10 Minutes of Reading Religious Text",
    "Participate in a Small Group",
    "Participate in Formal Gatherings",
    "Engage in Personal Devotion",
    "Other (when chosen provide free text)",
  ],

  SHSpiritualActivity: [
    "Pray",
    "Complete a Fast",
    "At Least 10 Minutes of Reading Religious Text",
    "Participate in a Ministry",
    "Participate in a Small Group",
    "Participate in Formal Gatherings",
    "Engage in Personal Devotion",
    "Other (when chosen provide free text)",
  ],

  SHReadText: [
    "Pray",
    "Complete a Fast",
    "At Least 10 Minutes of Reading Religious Text",
    "At Least 10 Minutes of Listening Religious Text",
    "Listen to / Sing Scripture Songs",
    "Engage in Personal Devotion",
    "Other (when chosen provide free text)",
  ],

  SHAlignText: [
    "Pray",
    "Complete a Fast",
    "Engage in Personal Devotion",
    "Other (when chosen provide free text)",
  ],

  SHCommunity: [
    "Pray",
    "Participate in a Group Fast",
    "Participate in a Ministry",
    "Participate in a Small Group",
    "Participate in Formal Gatherings",
    "Other (when chosen provide free text)",
  ],

  MHHope: [
    "Practice Setting Clear Expectations (No Frequency)",
    "Focus on One Step at a Time (No Frequency)",
    "Develop a Vision for Your Life (No Frequency)",
    "Journal Expectations and Fulfillment of Expectations (No Frequency)",
    "Practice Gratefulness",
    "Engage in Professional Individual Therapy",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  MHPeace: [
    "Resolve Conflicts (No Frequency)",
    "Establish and Maintain Healthy Boundaries (No Frequency)",
    "De-Clutter Living Space",
    "Practice Acknowledging Granular Emotions",
    "Practice Communicating Granular Emotions",
    "Spend Time in Nature (Stress management)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  SoHLearning: [
    "Mentor Someone",
    "Secure and Meet with a Mentor",
    "Listen to Others Thoughts",
    "Choose a Strength You Want to Cultivate and Practice It",
    "Seek Honest Feedback from Safe People and Practice Improving",
    "Learn Something New and Useful (when chosen provide free text)",
    "Complete Classes for Degree, License, or Certification (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  MHJoy: [
    "Practice Positive Thoughts (No Frequency)",
    "Practice Gratefulness",
    "Help Someone Else / Volunteer",
    "Mentor Someone",
    "Other (when chosen provide free text)",
  ],

  SoHStable: [
    "Avoid Spending Money Unnecessarily (No Frequency)",
    "Build a Positive Support System (No Frequency)",
    "Add Money to Savings",
    "Journal or Practice Expressive Writing",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  SoHSafety: [
    "Build a Positive Support System (No Frequency)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  SoHKindness: [
    "Practice Telling the Truth Gently (No Frequency)",
    "Journal or Practice Expressive Writing",
    "Other (when chosen provide free text)",
  ],

  SoHForgiveness: [
    "Apologize to Someone / Make Amends (No Frequency)",
    "Verbalize or Internalize Forgiving Yourself (No Frequency)",
    "Verbalize or Internalize Forgiving Someone Else (No Frequency)",
    "Journal or Practice Expressive Writing",
    "Other (when chosen provide free text)",
  ],

  SoHPatience: [
    "Practice Patience / Waiting with a Positive Attitude (No Frequency)",
    "Practice Gratefulness While Waiting (No Frequency)",
    "Other (when chosen provide free text)",
  ],

  SoHRelationships: [
    "Resolve Conflicts (No Frequency)",
    "Establish and Maintain Healthy Boundaries (No Frequency)",
    "Practice Communicating Granular Emotions (No Frequency)",
    "(Re)evaluate Social Circle / Support System (No Frequency)",
    "Mentor Someone",
    "Engage in Professional Family Therapy",
    "Complete a Book / Program on Healthy Relationships/Communication (when chosen provide free text)",
    "Engage Additional Support from Family or Friends (when chosen provide free text)",
    "Engage Additional Support from Faith Community (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  SoHBoundaries: [
    "Identify Healthy Boundaries (No Frequency)",
    "Enforce Healthy Boundaries (No Frequency)",
    "Develop a Vision for Your Life (No Frequency)",
    "Practice Acknowledging Granular Emotions (No Frequency)",
    "Practice Communicating Granular Emotions (No Frequency)",
    "Practice Having Challenging Conversations (No Frequency)",
    "Engage in Professional Individual Therapy",
    "Engage in Professional Family Therapy",
    "Complete a Book on Healthy Boundaries (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  SoHEUnpleasant: [
    "Practice Acknowledging Granular Emotions (No Frequency)",
    "Practice Communicating Granular Emotions (No Frequency)",
    "Journal or Practice Expressive Writing",
    "Engage in a Positive Coping Mechanism (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  SoHEPleasant: [
    "Practice Acknowledging Granular Emotions (No Frequency)",
    "Practice Communicating Granular Emotions (No Frequency)",
    "Practice Vulnerable Communication with Safe People (No Frequency)",
    "Help Someone Else / Volunteer",
    "Other (when chosen provide free text)",
  ],

  SoHEControl: [
    "Practice Acknowledging Granular Emotions (No Frequency)",
    "Practice Communicating Granular Emotions (No Frequency)",
    "Journal Emotions to See Trends / Patterns (No Frequency)",
    "Engage in Professional Individual Therapy",
    "Engage in Professional Family Therapy",
    "Complete a Support Group / Program (when chosen provide free text)",
    "Engage Additional Support from Referral (when chosen provide free text)",
    "Other (when chosen provide free text)",
  ],

  SoHContribution: [
    "Mentor Someone",
    "Engage in Meaningful Volunteer Work",
    "Participate in a Club or Interest Group",
    "Participate in a Community Initiative",
    "Participate in Mission or Disaster Relief Trip",
    "Other (when chosen provide free text)",
  ],
};
const labels = {
  // Coping Mechanisms
  CMAlcohol: "Alcohol Abuse",
  CMDrugs: "Drug Abuse",
  CMOvereating: "Overeating",
  CMSmoking: "Smoking",
  CMGaming: "Excessive Gaming",
  CMShopping: "Excessive Shopping",
  CMGambling: "Gambling",
  CMSex: "Casual Sex",
  CMPorn: "Pornography",
  CMBlaming: "Blaming Self or Others",
  CMHurting: "Hurting Self or Others",
  CMDisengage: "Disengagement / Detachment / Self Isolation",
  CMArt: "Drawing/Painting/Art",
  CMMusic: "Music",
  CMPoetry: "Poetry/Poetical Lyrics",
  CMReading: "Reading",
  CMGroups: "Group Discussion",
  CMCounseling: "Individual Counseling",
  CMVenting: "Talking/Venting",
  CMWriting: "Writing/Journaling",
  CMSensory: "Sensory/Fidgets",
  CMDancing: "Dancing",
  CMExercising: "Exercising",
  CMWalking: "Walking",
  CMChange: "Change Something for Different Results",
  CMAnalyze: "Analyze the Situation for Better Understanding",
  CMDaydream: "Daydream or Imagine a Better Situation",
  CMPositive: "Focus on the Positives",

  // Mental Health
  MHSleeplessness: "Sleeplessness",
  MHAlcoholUse: "Alcohol Use",
  MHAnxiety: "Fight, Flight, Freeze",
  MHDepression: "Depression",
  MHDrugUse: "Drug Use",
  MHGrief: "Grief",
  MHGuilt: "Guilt",
  MHIrritability: "Irritability",
  MHStress: "Stress",
  MHRegret: "Regret",
  MHSuicidalThoughts: "Suicidal Thoughts",
  MHLoneliness: "Loneliness",
  MHWorry: "Worry / Anxiety",

  // Physical Health
  PHVegeFruits: "Vegetables and Fruit",
  PHBeanLentils: "Beans and Lentils",
  PHGrainBreads: "Grains and Breads",
  PHDairy: "Dairy",
  PHMeat: "Animal Meat",
  PHFishSeafood: "Fish/Seafood",
  PHSweets: "Sweets/Desserts",
  PHWater: "Water",
  PHPhysicalActivity: "Physically Active",

  // Spiritual Health
  SHSpiritualDefine: "God or a Higher Power Loves Me",
  SHSpiritualIntegrate: "Integrating Spirituality Into Life",
  SHPrayer: "Prayer",
  SHSpiritualActivity: "Communication With Like-Minded Individuals",
  SHReadText: "Reading Religious Text",
  SHAlignText: "Aligning Life With Religious Texts",
  SHCommunity: "Participating in Religious Gatherings / Groups / Prayer",

  // Social Health
  MHHope: "Hopelessness",
  MHPeace: "Inner Turmoil",
  SoHLearning: "Willingness to Learn and Be Corrected",
  MHJoy: "Emptiness",
  SoHStable: "Stability (e.g., Housing, Finances, etc.)",
  SoHSafety: "Safety and Security",
  SoHKindness: "Kindness Toward Others",
  SoHForgiveness: "Forgiveness of Yourself and Others",
  SoHPatience: "Patience With Yourself and Others",
  SoHRelationships: "Support in Your Relationships",
  SoHContribution: "Contributing to Others / Your Community",
  SoHBoundaries: "Maintaining Healthy Boundaries",
  SoHEUnpleasant: "Unpleasant Emotions",
  SoHEPleasant: "Pleasant Emotions",
  SoHEControl: "Control Over Your Emotions",

  // Labs – Lipids
  LabsTG: "Triglycerides",
  LabsTC: "Cholesterol (Total)",
  LabsHDL: "Cholesterol (HDL)",
  LabsLDL: "Cholesterol (LDL)",

  // Labs – Minerals
  LabsNa: "Sodium",
  LabsK: "Potassium",
  LabsP: "Phosphorus",
  LabsCl: "Chloride",
  LabsZn: "Zinc",
  LabsCa: "Calcium",
  LabsMg: "Magnesium",
  LabsFe: "Iron",
  LabsFerritin: "Ferritin",

  // Labs – Vitamins
  LabsVitA: "Vitamin A",
  LabsVitB1: "Thiamin (B1)",
  LabsVitB2: "Riboflavin (B2)",
  LabsVitB3: "Niacin (B3)",
  LabsVitB5: "Pantothenic Acid (B5)",
  LabsVitB6: "Pyridoxine (B6)",
  LabsVitB7: "Biotin (B7)",
  LabsVitB9: "Folate (B9)",
  LabsVitB12: "Cobalamin (B12)",
  LabsVitC: "Vitamin C",
  LabsVitD: "Vitamin D, 25-Hydroxy",
  LabsVitE: "Vitamin E (Alpha Tocopherol)",
  LabsVitK: "Vitamin K",
};
const domainMap = {
  CM: "Coping Mechanisms",
  MH: "Mental Health",
  PH: "Physical Health",
  SH: "Spiritual Health",
  SoH: "Social Health",
  SDoH: "Social Determinants of Health",
  TR: "Trauma",
};

function getDomainName(key) {
  // 1) Labs use the full key
  if (lipidLabKeys.includes(key)) return "Lipids";
  if (mineralLabKeys.includes(key)) return "Minerals";
  if (vitaminLabKeys.includes(key)) return "Vitamins";

  // 2) Social determinants use the SDoH prefix
  if (key.startsWith("SDoH")) return domainMap["SDoH"];

  // 3) Social health uses its three-letter prefix; other domains use two letters.
  if (key.startsWith("SoH")) return domainMap.SoH;

  // 4) Everything else uses the 2-letter prefix (CM, MH, PH, SH, TR)
  const prefix = key.slice(0, 2); // "CMAlcohol" -> "CM", "MHStress" -> "MH", etc.
  return domainMap[prefix] || "Unknown";
}

const client = generateClient();

function encodeBarrierResponseNote(otherBarrierText, barrierNote) {
  const other = (otherBarrierText || "").trim();
  const response = (barrierNote || "").trim();
  if (other && response)
    return `Other barrier: ${other}\n\nBarrier response: ${response}`;
  if (other) return `Other barrier: ${other}`;
  return response;
}

function parseBarrierResponseNote(note, isOtherSelected) {
  const raw = typeof note === "string" ? note : "";
  const trimmed = raw.trim();
  if (!trimmed) return { otherBarrierText: "", barrierNote: "" };

  const hasOther = /(^|\n)\s*Other barrier:\s*/i.test(trimmed);
  const hasResponse = /(^|\n)\s*Barrier response:\s*/i.test(trimmed);

  if (!hasOther && !hasResponse) {
    if (isOtherSelected) return { otherBarrierText: trimmed, barrierNote: "" };
    return { otherBarrierText: "", barrierNote: trimmed };
  }

  const otherMatch = trimmed.match(
    /(^|\n)\s*Other barrier:\s*([\s\S]*?)(?=(\n\s*Barrier response:\s*)|$)/i,
  );
  const responseMatch = trimmed.match(
    /(^|\n)\s*Barrier response:\s*([\s\S]*?)$/i,
  );

  return {
    otherBarrierText: (otherMatch?.[2] || "").trim(),
    barrierNote: (responseMatch?.[2] || "").trim(),
  };
}

function CopingPlanCard({
  categoryKey,
  actions,
  onPlanUpdate,
  maxAllowed,
  totalSelectedCount,
  savedActionCount,
  resetFlag,
  existingCount,
}) {
  const [visible, setVisible] = useState(false);
  const [selectedActions, setSelectedActions] = useState({});
  const [frequencies, setFrequencies] = useState({});
  const [notes, setNotes] = useState({});
  const [otherInputs, setOtherInputs] = useState({});
  const [flipped, setFlipped] = useState({});
  const [warningIndex, setWarningIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    setSelectedActions({});
    setFrequencies({});
    setNotes({});
    setOtherInputs({});
    setFlipped({});
  }, [resetFlag]);

  const toggleAction = (index) => {
    const newState = !selectedActions[index];
    const currentSelectedCount =
      Object.values(selectedActions).filter(Boolean).length;
    const newTotal =
      existingCount +
      (newState ? currentSelectedCount + 1 : currentSelectedCount - 1);
    const alreadySelected = selectedActions[index];

    const newSelectedCount =
      Object.values(selectedActions).filter(Boolean).length + 1; // assume trying to add one
    if (!alreadySelected && totalSelectedCount + 1 > maxAllowed) {
      setWarningIndex(index);
      setTimeout(() => setWarningIndex(null), 12000);
      return;
    }

    setSelectedActions((prev) => ({ ...prev, [index]: newState }));
  };

  const selected = useMemo(() => {
    return Object.entries(selectedActions)
      .filter(([_, isSelected]) => isSelected)
      .map(([index]) => {
        const rawAction = actions[index];
        const clean = rawAction.replace(" (when chosen provide free text)", "");
        return {
          action: otherInputs[index] || clean,
          frequency: frequencies[index] || "",
          note: notes[index] || "",
          needsNote: rawAction
            .toLowerCase()
            .includes("(when chosen provide free text)"),
        };
      });
  }, [selectedActions, frequencies, notes, otherInputs, actions]);

  useEffect(() => {
    onPlanUpdate(categoryKey, selected);
  }, [selected, categoryKey, onPlanUpdate]);

  const safetyPlanCategories = [
    "MHSuicidalThoughts",
    "CMAlcohol",
    "CMDrugs",
    "CMSex",
    "CMPorn",
    "CMHurting",
  ];

  const buttonLabel = visible
    ? "Hide"
    : safetyPlanCategories.includes(categoryKey)
      ? "Create Safety Plan"
      : "Improve Your Score";

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          maxWidth: "100%",
          margin: "40px auto",
          padding: 20,
          border: "1px solid #ccc",
          borderRadius: 12,
        }}
      >
        <h2
          style={{
            textAlign: "center",
            marginBottom: 12,
            wordWrap: "break-word",
            overflowWrap: "break-word",
            hyphens: "auto",
          }}
        >
          {labels[categoryKey] || categoryKey}
        </h2>

        <button
          onClick={() => setVisible((v) => !v)}
          style={{
            backgroundColor: safetyPlanCategories.includes(categoryKey)
              ? "#8C1456"
              : "#D4B65F",
            color: "white",
            padding: "10px 20px",
            border: "none",
            borderRadius: 10,
            width: "100%",
            cursor: "pointer",
            marginBottom: 20,
            fontWeight: "bold",
            fontSize: 16,
          }}
        >
          {buttonLabel}
        </button>

        {visible &&
          actions.map((action, index) => {
            const isOther = action.toLowerCase().startsWith("other");
            const needsNote = action
              .toLowerCase()
              .includes("when chosen provide free text");
            const noFrequency = action.toLowerCase().includes("(no frequency)"); // detect "(No Frequency)"
            const isBookProgramAction =
              /complete\s+a\s+.*book\s*\/\s*program/i.test(action);
            const isActive =
              selectedActions[index] || (isOther && otherInputs[index]);
            // Clean action label removes both tags:
            const cleanAction = action
              .replace(/\(when chosen provide free text\)/i, "")
              .replace(/\(No Frequency\)/i, "")
              .replace(/\(info text\)/i, "")
              .replace(/\([^)]+\)/g, "") // remove any remaining ()
              .trim();

            return (
              <div key={index} style={{ marginBottom: 20 }}>
                {isOther ? (
                  <div
                    style={{
                      display: "grid",
                      transition: "transform 0.6s",
                      transformStyle: "preserve-3d",
                      transform: flipped[index] ? "rotateY(180deg)" : "none",
                    }}
                  >
                    {/* FRONT side (default view) */}
                    <div
                      onClick={() => toggleAction(index)}
                      style={{
                        backfaceVisibility: "hidden",
                        border: "2px solid",
                        padding: "10px 12px",
                        borderRadius: 12,
                        borderColor: isActive ? "#0067B3" : "#ccc",
                        background: isActive ? "#d9eaff" : "#fff",
                        textAlign: "left",
                        cursor: "pointer",
                        boxShadow: isActive ? "0 0 10px #0067B3aa" : "none",
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      {/* Text area */}
                      <div
                        style={{
                          flex: 1,
                          paddingRight: 10,
                          fontWeight: "normal",
                          minWidth: 0,
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        }}
                      >
                        Other
                      </div>

                      {/* Info tab */}
                      <div
                        onClick={(e) => {
                          e.stopPropagation(); // prevent toggle
                          setFlipped((prev) => ({ ...prev, [index]: true }));
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 25,
                          height: 48,
                          fontSize: 16,
                          color: "#0067B3",
                          cursor: "pointer",
                          fontWeight: "bold",
                          userSelect: "none",
                          borderLeft: "1px solid #0067B3",
                          borderRadius: "0 12px 12px 0",
                          flexShrink: 0,
                          paddingLeft: 10,
                        }}
                        title="More Info"
                      >
                        ⓘ
                      </div>
                    </div>

                    {/* BACK side (flipped view) */}
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        padding: "10px 12px",
                        borderRadius: 12,
                        background: "#f0f8ff",
                        border: "2px solid #0077cc",
                        backfaceVisibility: "hidden",
                        transform: "rotateY(180deg)",
                        textAlign: "center",
                        cursor: "pointer",
                        minHeight: "100%",
                        maxHeight: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                      onClick={() =>
                        setFlipped((prev) => ({ ...prev, [index]: false }))
                      }
                    >
                      <div style={{ fontSize: "80%" }}>
                        <strong>More Info:</strong> Click Other to customize
                        your action below.
                      </div>
                      <div
                        style={{
                          fontSize: 9,
                          marginTop: 2,
                          color: "#0067B3",
                          textAlign: "center",
                        }}
                      >
                        (Click to return)
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      perspective: "1000px",
                      position: "relative",
                      height: "auto",
                      minHeight: 60,
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        transition: "transform 0.6s",
                        transformStyle: "preserve-3d",
                        transform: flipped[index] ? "rotateY(180deg)" : "none",
                      }}
                    >
                      {/* FRONT side (default view) */}
                      <div
                        onClick={() => toggleAction(index)}
                        style={{
                          backfaceVisibility: "hidden",
                          border: "2px solid",
                          padding: "10px 12px",
                          borderRadius: 12,
                          borderColor: isActive ? "#0067B3" : "#ccc",
                          background: isActive ? "#d9eaff" : "#fff",
                          textAlign: "left",
                          cursor: "pointer",
                          boxShadow: isActive ? "0 0 10px #0067B3aa" : "none",
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        {/* Text area */}
                        <div
                          style={{
                            flex: 1,
                            paddingRight: 10,
                            fontWeight: "normal",
                            minWidth: 0,
                            whiteSpace: "normal",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          <div>{cleanAction}</div>
                        </div>

                        {/* Info tab */}
                        <div
                          onClick={(e) => {
                            e.stopPropagation(); // prevent toggle
                            setFlipped((prev) => ({ ...prev, [index]: true }));
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 25,
                            height: 48,
                            fontSize: 16,
                            color: "#0067B3",
                            cursor: "pointer",
                            fontWeight: "bold",
                            userSelect: "none",
                            borderLeft: "1px solid #0067B3",
                            borderRadius: "0 12px 12px 0",
                            flexShrink: 0,
                            paddingLeft: 10,
                          }}
                          title="More Info"
                        >
                          ⓘ
                        </div>
                      </div>

                      {/* BACK side (flipped view) */}
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          padding: "10px 12px",
                          borderRadius: 12,
                          background: "#f0f8ff",
                          border: "2px solid #0077cc",
                          backfaceVisibility: "hidden",
                          transform: "rotateY(180deg)",
                          textAlign: "center",
                          cursor: "pointer",
                          minHeight: "100%",
                          maxHeight: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                        }}
                        onClick={() =>
                          setFlipped((prev) => ({ ...prev, [index]: false }))
                        }
                      >
                        <div style={{ fontSize: "80%" }}>
                          <strong>More Info:</strong>{" "}
                          {(() => {
                            const infoText =
                              action
                                .match(/\(([^)]+)\)/g)
                                ?.map((s) => s.slice(1, -1))
                                ?.filter(
                                  (s) =>
                                    !/no frequency|free text|info text/i.test(
                                      s.toLowerCase(),
                                    ) && s.length > 2,
                                )
                                ?.join(" ") || "";

                            if (infoText) return infoText;

                            if (isBookProgramAction) {
                              return (
                                <>
                                  This could be the{" "}
                                  <a
                                    href="https://a.co/d/02Iv8KZl"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: "underline" }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Wholistic Health Program ©
                                  </a>
                                  .
                                </>
                              );
                            }

                            return "No additional information.";
                          })()}
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            marginTop: 2,
                            color: "#0067B3",
                            textAlign: "center",
                          }}
                        >
                          (Click to return)
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {warningIndex === index && (
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 13,
                      color: "#b00000",
                      fontWeight: "bold",
                    }}
                  >
                    You have {existingCount} finalized action
                    {savedActionCount === 1 ? "" : "s"}. You can only select{" "}
                    {maxAllowed}. (Max 10)
                  </div>
                )}

                {isActive && (
                  <>
                    {/* Only show frequency dropdown if NOT noFrequency */}
                    {!noFrequency && (
                      <select
                        style={{
                          width: "100%",
                          marginTop: 8,
                          padding: 10,
                          borderRadius: 8,
                          border: "1px solid #ccc",
                          fontSize: 14,
                        }}
                        value={frequencies[index] || ""}
                        onChange={(e) =>
                          setFrequencies((prev) => ({
                            ...prev,
                            [index]: e.target.value,
                          }))
                        }
                      >
                        <option value="">Select Frequency</option>
                        <option>1x Per Day</option>
                        <option>1x Per Week</option>
                        <option>2x Per Week</option>
                        <option>3x Per Week</option>
                        <option>5x Per Week</option>
                        <option>1x Per Month</option>
                        <option>2x Per Month</option>
                        <option>3x Per Month</option>
                        <option>1x Per Year</option>
                        <option>2x Per Year</option>
                        <option>1x Per Quarter</option>
                      </select>
                    )}

                    {needsNote && (
                      <input
                        type="text"
                        placeholder="Add a note..."
                        value={notes[index] || ""}
                        onChange={(e) =>
                          setNotes((prev) => ({
                            ...prev,
                            [index]: e.target.value,
                          }))
                        }
                        style={{
                          marginTop: 8,
                          width: "100%",
                          padding: 10,
                          borderRadius: 8,
                          border: "1px solid #ccc",
                          fontSize: 14,
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}
const createTrackPlanMutation = `
  mutation CreateTrackPlan($input: CreateTrackPlanInput!) {
    createTrackPlan(input: $input) {
      id
    }
  }
`;

export async function fetchFlaggedData(
  setSelectedCategories,
  setUserId,
  setAllPlans,
  setExistingCount,
  setSavedActionCount,
  setRemainingSlots,
  setLoading,
  setError,
) {
  try {
    setLoading(true);
    setError(null);
    const attrs = await fetchUserAttributes();
    const uid = attrs.sub;
    if (!uid) throw new Error("User not authenticated");

    setUserId(uid);

    // Auto-flagging is advisory data. Query failures should not block the page.
    async function run(label, q, vars = undefined) {
      try {
        const res = await client.graphql({
          query: q,
          variables: vars,
        });
        if (res?.errors && res.errors.length) {
          console.error(`${label} GraphQL errors:`, res.errors);
          return null;
        }
        return res;
      } catch (err) {
        console.error(`${label} request failed:`, err);
        return null;
      }
    }

    const trackPlanQuery = `
      query ListTrackPlans {
        listTrackPlans(filter: {
          or: [{ owner: { eq: "${uid}" } }, { userId: { eq: "${uid}" } }],
          Discontinued: { eq: false },
          Successful: { eq: false }
        }, limit: 100) {
          items { id }
        }
      }
    `;

    const assessmentQuery = `
      query ListAssessments {
        listAssessmentScores(limit: 50, filter: {
          or: [{ owner: { eq: "${uid}" } }, { userId: { eq: "${uid}" } }],
          isActive: { eq: false }
        }) {
          items { id updatedAt }
        }
      }
    `;

    const assessmentRes = await run(
      "fetchFlaggedData:listAssessmentScores",
      assessmentQuery,
    );
    const items = assessmentRes?.data?.listAssessmentScores?.items || [];

    const trackPlanRes = await run(
      "fetchFlaggedData:listTrackPlans",
      trackPlanQuery,
    );
    const existingItems = trackPlanRes?.data?.listTrackPlans?.items || [];

    const maxTotalAllowed = 10;
    const remaining = maxTotalAllowed - existingItems.length;

    setExistingCount(existingItems.length);
    setSavedActionCount(existingItems.length);
    setRemainingSlots(remaining);

    const flagged = [];

    // 1) Flag from latest AssessmentScores (if any)
    if (items.length > 0) {
      const latest = items.sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
      )[0];

      // Only request fields that actually exist on AssessmentScores.
      // Exclude lab keys which belong to LabResults, not AssessmentScores.
      const assessmentFieldKeys = Object.keys(Actions).filter(
        (k) => !allLabKeys.includes(k) && isAssessmentScoreActionKey(k),
      );
      const selection = assessmentFieldKeys.length
        ? assessmentFieldKeys.join("\n")
        : "id";
      const getQuery = `
        query GetAssessment {
          getAssessmentScores(id: "${latest.id}") {
            ${selection}
          }
        }
      `;
      const getRes = await run(
        "fetchFlaggedData:getAssessmentScores",
        getQuery,
      );
      const record = getRes?.data?.getAssessmentScores;
      if (record) {
        Object.entries(record).forEach(([k, v]) => {
          if (Object.keys(Actions).includes(k)) {
            if (Number(v) >= 2) {
              flagged.push(k);
            }
          }
        });
      }
    }

    // 2) Flag from latest LabResults (out-of-range values)
    const labQuery = `
      query ListLabResults {
        listLabResults(limit: 50, filter: {
          or: [{ owner: { eq: "${uid}" } }, { userId: { eq: "${uid}" } }]
        }) {
          items {
            id
            CompletedDate
            CompletedTime
            ${allLabKeys.join("\n")}
            createdAt
          }
        }
      }
    `;

    const labRes = await run("fetchFlaggedData:listLabResults", labQuery);
    const labItems = labRes?.data?.listLabResults?.items || [];

    if (labItems.length > 0) {
      const latestLab = [...labItems].sort((a, b) => {
        const dA = new Date(a.CompletedDate || a.createdAt || 0);
        const dB = new Date(b.CompletedDate || b.createdAt || 0);
        return dB - dA;
      })[0];

      allLabKeys.forEach((key) => {
        const raw = latestLab[key];
        if (isLabValueOutOfRange(key, raw)) {
          if (!flagged.includes(key)) flagged.push(key);
        }
      });
    }

    setSelectedCategories(flagged);
  } catch (err) {
    // Only auth/bootstrap failures should land here.
    console.error("fetchFlaggedData fatal error (details):", err);
    setSelectedCategories([]);
    setError(null);
  } finally {
    setLoading(false);
  }
}

export default function CopingPlanManager() {
  // States
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allPlans, setAllPlans] = useState({});
  const [userId, setUserId] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [savedActionCount, setSavedActionCount] = useState(0);
  const [remainingSlots, setRemainingSlots] = useState(10);
  const [resetFlag, setResetFlag] = useState(false);
  const [existingCount, setExistingCount] = useState(0);
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [coupon, setCoupon] = useState("");
  const [couponError, setCouponError] = useState("");
  const [couponData, setCouponData] = useState(null);
  const [validating, setValidating] = useState(false);
  const hasExistingPlans = existingCount > 0;
  const basePrice = 10.0;
  const [selectedStrengths, setSelectedStrengths] = useState([]);

  const [selectedBarriers, setSelectedBarriers] = useState([]);
  const [otherBarrierText, setOtherBarrierText] = useState("");
  const isOtherBarrierSelected = selectedBarriers.some(
    (item) => item.value === "BROther",
  );
  const [selectedNoteTriggers, setSelectedNoteTriggers] = useState([]);
  const [selectedNoteWarningSigns, setSelectedNoteWarningSigns] = useState([]);
  const [barrierNote, setBarrierNote] = useState("");

  const [savingSelections, setSavingSelections] = useState(false);
  const [selectionsSaveError, setSelectionsSaveError] = useState("");
  const [selectionsSavedAt, setSelectionsSavedAt] = useState(null);

  // Persist dropdown selections between visits.
  const createPlanRecordRef = useRef(null);
  const didHydrateCreatePlanRef = useRef(false);
  const autosaveTimerRef = useRef(null);
  const lastAutosaveFingerprintRef = useRef("");
  const specialCategories = [
    "MHSuicidalThoughts",
    "CMAlcohol",
    "CMDrugs",
    "CMSex",
    "CMPorn",
    "CMHurting",
  ];

  const CREATE_PLAN_ROLLOVER_DAYS = 365;
  const FREE_TRIAL_CODE = "FREETRIAL";
  const FREE_TRIAL_DAYS = 14;
  const MS_PER_DAY = 24 * 60 * 60 * 1000;

  const getCreatePlanCreatedAtMs = (plan) => {
    const createdAt = plan?.createdAt;
    const t = createdAt ? new Date(createdAt).getTime() : NaN;
    return Number.isFinite(t) ? t : null;
  };

  const isCreatePlanWithinDays = (plan, days) => {
    const createdMs = getCreatePlanCreatedAtMs(plan);
    if (!Number.isFinite(days) || !createdMs) return false;
    const ageMs = Date.now() - createdMs;
    return ageMs >= 0 && ageMs < days * MS_PER_DAY;
  };

  const getExpiryDateFromNow = (days) => {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + days);
    return expiryDate;
  };

  async function resolveCouponByCode(inputCode) {
    const code = String(inputCode || "").trim();
    if (!code) return null;

    const candidateCodes = Array.from(
      new Set([code, code.toUpperCase(), code.toLowerCase()]),
    );

    for (const candidate of candidateCodes) {
      try {
        const res = await client.graphql({
          query: getCoupon,
          variables: { id: candidate },
        });
        const coupon = res?.data?.getCoupon || res?.data?.GetCoupon || null;
        if (coupon) return coupon;
      } catch (err) {
        // If a malformed Coupon record breaks the generated query, fall back below.
      }
    }

    if (code.toUpperCase() !== FREE_TRIAL_CODE) return null;

    const listRes = await client.graphql({
      query: `query ListCouponsMinimal($limit: Int) {\n  listCoupons(limit: $limit) {\n    items {\n      id\n      type\n      value\n      isActive\n      createdAt\n      updatedAt\n    }\n  }\n}`,
      variables: { limit: 100 },
    });
    const coupons = listRes?.data?.listCoupons?.items || [];
    const normalized = code.toLowerCase();
    return (
      coupons.find(
        (item) =>
          String(item?.id || "")
            .trim()
            .toLowerCase() === normalized,
      ) || null
    );
  }

  async function grantCouponAccess(couponRecord) {
    if (!userId) throw new Error("User not authenticated");

    const couponCode = String(couponRecord?.id || "")
      .trim()
      .toUpperCase();
    if (!couponCode) throw new Error("Invalid coupon");

    const isFreeTrialCoupon = couponCode === FREE_TRIAL_CODE;
    const entitlementDays = isFreeTrialCoupon ? FREE_TRIAL_DAYS : 365;

    if (isFreeTrialCoupon) {
      const priorUseRes = await client.graphql({
        query: listUserPayments,
        variables: {
          filter: {
            and: [
              {
                or: [{ owner: { eq: userId } }, { userId: { eq: userId } }],
              },
              { couponCode: { eq: couponCode } },
            ],
          },
          limit: 10,
        },
      });

      const priorUse = priorUseRes?.data?.listUserPayments?.items?.[0] || null;
      if (priorUse) {
        throw new Error(
          "This free trial coupon has already been used for this account.",
        );
      }
    }

    const paidUntilDate = getExpiryDateFromNow(entitlementDays);

    await client.graphql({
      query: createUserPayments,
      variables: {
        input: {
          userId,
          paymentType: "one-time",
          subscriptionId: `FREE-TRIAL-${Date.now()}`,
          paymentStatus: "completed",
          couponCode,
          couponPercent: 100,
          paidUntil: paidUntilDate.toISOString(),
          paymentFor: "Individual",
        },
      },
    });

    try {
      await client.graphql({
        query: updateCoupon,
        variables: {
          input: {
            id: couponRecord.id,
            timesUsed: (couponRecord.timesUsed || 0) + 1,
            _version: couponRecord._version,
          },
        },
      });
    } catch (err) {
      console.warn(
        "Failed to update coupon usage after free trial grant:",
        err,
      );
    }

    setHasAccess(true);
    await fetchFlaggedData(
      setSelectedCategories,
      setUserId,
      setAllPlans,
      setExistingCount,
      setSavedActionCount,
      setRemainingSlots,
      setLoading,
      setError,
    );
    setCouponError("");
    setSuccessMsg(
      isFreeTrialCoupon
        ? `Free trial coupon applied! Access granted for ${FREE_TRIAL_DAYS} days.`
        : "100% coupon applied! Access granted for 1 year.",
    );
  }

  const pickMostRecentCreatePlan = (plans) => {
    if (!Array.isArray(plans) || plans.length === 0) return null;
    const sorted = [...plans].sort((a, b) => {
      const dA = new Date(a?.updatedAt || a?.createdAt || 0);
      const dB = new Date(b?.updatedAt || b?.createdAt || 0);
      return dB - dA;
    });
    return sorted[0] || null;
  };

  const pickActiveCreatePlan = (plans) => {
    const withinWindow = (Array.isArray(plans) ? plans : []).filter((p) =>
      isCreatePlanWithinDays(p, CREATE_PLAN_ROLLOVER_DAYS),
    );
    return pickMostRecentCreatePlan(withinWindow);
  };
  async function loadCreatePlan(primaryUserId) {
    try {
      // CreatePlan records may have been saved historically with different
      // identifiers (sub vs username vs email) in `userId` and/or `owner`.
      // Query broadly so returning users see their saved dropdown selections.
      const candidateIds = [];
      if (primaryUserId) candidateIds.push(primaryUserId);

      try {
        const attrs = await fetchUserAttributes();
        if (attrs?.sub) candidateIds.push(attrs.sub);
        if (attrs?.email) candidateIds.push(attrs.email);
      } catch (e) {
        // ignore
      }

      const currentUser = await getCurrentUser().catch(() => null);
      if (currentUser?.username) candidateIds.push(currentUser.username);
      if (currentUser?.userId) candidateIds.push(currentUser.userId);
      if (currentUser?.id) candidateIds.push(currentUser.id);

      const uniqueIds = Array.from(
        new Set(candidateIds.filter((v) => typeof v === "string" && v.trim())),
      );
      if (uniqueIds.length === 0) return null;

      const res = await client.graphql({
        query: listCreatePlans,
        variables: {
          filter: {
            or: uniqueIds.map((id) => ({ userId: { eq: id } })),
          },
          limit: 50,
        },
      });

      const items = res?.data?.listCreatePlans?.items || [];
      if (items.length === 0) return null; // no plan found

      const activePlan = pickActiveCreatePlan(items);
      const latestPlan = pickMostRecentCreatePlan(items);

      // If there is no record within the last 365 days, we still return the
      // latest plan for display/prefill, but we DO NOT attach it for updates.
      return {
        planForDisplay: activePlan || latestPlan,
        planForUpdate: activePlan || null,
      };
    } catch (err) {
      // Print GraphQL errors if present for easier debugging
      if (err && err.errors)
        console.error(
          "Failed to load create plan (graphql errors):",
          err.errors,
        );
      else console.error("Failed to load create plan:", err);
      return null;
    }
  }

  async function saveCreatePlanSelections() {
    setSelectionsSaveError("");
    setSavingSelections(true);

    try {
      const candidateIds = [];
      let primaryId = null;
      try {
        const attrs = await fetchUserAttributes();
        if (attrs?.sub) {
          primaryId = attrs.sub;
          candidateIds.push(attrs.sub);
        }
        if (attrs?.email) candidateIds.push(attrs.email);
      } catch (e) {
        // ignore
      }

      const currentUser = await getCurrentUser().catch(() => null);
      if (!primaryId) {
        primaryId =
          currentUser?.userId ||
          currentUser?.username ||
          currentUser?.id ||
          null;
      }
      if (currentUser?.username) candidateIds.push(currentUser.username);
      if (currentUser?.userId) candidateIds.push(currentUser.userId);
      if (currentUser?.id) candidateIds.push(currentUser.id);

      const uniqueIds = Array.from(
        new Set(candidateIds.filter((v) => typeof v === "string" && v.trim())),
      );
      if (!primaryId) throw new Error("User not authenticated");

      // keep component state aligned
      if (!userId) setUserId(primaryId);

      let organizationId = null;
      try {
        const attrs = await fetchUserAttributes();
        const rawOrgId =
          attrs["custom:organizationId"] || attrs["custom:organizationID"];
        const accountType = attrs["custom:accountType"] || "";
        organizationId =
          rawOrgId || (accountType === "Organization" ? attrs.sub : null);
      } catch (e) {
        organizationId = null;
      }

      const input = mapSelectionsToCreatePlanInput({
        selectedStrengths: selectedStrengths || [],
        selectedBarriers: selectedBarriers || [],
        otherBarrierText,
        selectedNoteTriggers: selectedNoteTriggers || [],
        selectedNoteWarningSigns: selectedNoteWarningSigns || [],
        barrierNote,
        userId: primaryId,
      });

      if (organizationId && !input.organizationId) {
        input.organizationId = organizationId;
      }

      // Find latest CreatePlan record for any identifier
      const listRes = await client.graphql({
        query: listCreatePlans,
        variables: {
          filter: {
            or: uniqueIds.map((id) => ({ userId: { eq: id } })),
          },
          limit: 50,
        },
      });
      if (listRes?.errors?.length) {
        throw new Error(listRes.errors.map((e) => e.message).join("; "));
      }
      const items = listRes?.data?.listCreatePlans?.items || [];
      const planToUpdate = pickActiveCreatePlan(items);

      let saved = null;
      if (planToUpdate?.id) {
        const updateRes = await client.graphql({
          query: updateCreatePlan,
          variables: {
            input: {
              id: planToUpdate.id,
              ...input,
            },
          },
        });
        if (updateRes?.errors?.length) {
          throw new Error(updateRes.errors.map((e) => e.message).join("; "));
        }
        saved = updateRes?.data?.updateCreatePlan || null;
      } else {
        const createRes = await client.graphql({
          query: createCreatePlan,
          variables: { input },
        });
        if (createRes?.errors?.length) {
          throw new Error(createRes.errors.map((e) => e.message).join("; "));
        }
        saved = createRes?.data?.createCreatePlan || null;
      }

      if (!saved?.id) {
        throw new Error("Save did not return a CreatePlan id");
      }

      // Verify it exists by immediately reading it back.
      const verifyRes = await client.graphql({
        query: getCreatePlan,
        variables: { id: saved.id },
      });
      if (verifyRes?.errors?.length) {
        throw new Error(verifyRes.errors.map((e) => e.message).join("; "));
      }
      const verified = verifyRes?.data?.getCreatePlan || null;
      if (!verified?.id) {
        throw new Error("Saved CreatePlan could not be reloaded");
      }

      console.log("CreatePlan saved to backend:", {
        id: saved.id,
        userId: primaryId,
      });

      createPlanRecordRef.current = saved;
      setSelectionsSavedAt(new Date());

      // Keep autosave fingerprint in sync so it doesn't immediately retry.
      const fingerprint = JSON.stringify({
        strengths: (selectedStrengths || []).map((o) => o.value).sort(),
        barriers: (selectedBarriers || []).map((o) => o.value).sort(),
        noteTriggers: (selectedNoteTriggers || []).map((o) => o.value).sort(),
        noteWarningSigns: (selectedNoteWarningSigns || [])
          .map((o) => o.value)
          .sort(),
        otherBarrierText: (otherBarrierText || "").trim(),
        barrierNote: (barrierNote || "").trim(),
      });
      lastAutosaveFingerprintRef.current = fingerprint;
    } catch (err) {
      console.error("Save CreatePlan selections failed:", err);
      const msg =
        err?.errors?.map?.((e) => e.message).join("; ") ||
        err?.message ||
        "Failed to save selections";
      setSelectionsSaveError(msg);
    } finally {
      setSavingSelections(false);
    }
  }
  function flattenOptions(optionGroups) {
    return optionGroups.reduce((acc, group) => {
      return acc.concat(group.options);
    }, []);
  }

  function parseCreatePlanToSelections(plan) {
    if (!plan)
      return {
        strengths: [],
        barriers: [],
        otherBarrierText: "",
        noteTriggers: [],
        noteWarningSigns: [],
        barrierNote: "",
      };

    const strengthKeys = [
      "PSProblemSolving",
      "PSSelfEsteem",
      "PSChoiceAutonomy",
      "PSSelfCare",
      "PSSelfAwareness",
      "PSCreativity",
      "CSForgiveness",
      "CSKindness",
      "CSGratitude",
      "CSPatience",
      "CSCourage",
      "CMSCopingChoice",
      "CMSResilience",
      "CMSSelfRegulation",
      "CMSOptimism",
      "COMSupportSystem",
      "COMSocialIntelligence",
      "COMCommunication",
      "COMFaithCommunity",
      "COMAccomplishments",
    ];

    const barrierKeys = [
      "BRStableHousing",
      "BRQualityHealthcare",
      "BRProfessionalAccess",
      "BRInsuranceCoverage",
      "BRFinancialConstraints",
      "BREducationAttainment",
      "BRSocialStigma",
      "BRUnsupportiveCommunity",
      "BRNonexistentCommunity",
      "BRCommunityResources",
      "BRProfessionalTrust",
      "BRHealthcareSystemTrust",
      "BRCognitiveImpairments",
      "BRDependentCare",
      "BRTransportation",
      "BRTechnologyAccess",
      "BRLanguage",
      "BRHealthcareProcess",
      "BROther",
    ];

    const noteTriggerKeys = [
      "TGBetrayal",
      "TGBoundaries",
      "TGDisorder",
      "TGPain",
      "TGRejection",
      "TGStress",
      "TGMemories",
      "TGUnjustTreatment",
      "TGUnpleasantConversations",
      "TGUnsafeEnvironments",
      "TGOther",
    ];

    const noteWarningSignKeys = [
      "WSFlashbacks",
      "WSIncreasedAppetite",
      "WSDecreasedAppetite",
      "WSIntenseEmotions",
      "WSDisconnecting",
      "WSTime",
      "WSNegativeSelfTalk",
      "WSNeglectingSelfCare",
      "WSNightmares",
      "WSRecklessBehavior",
      "WSOther",
    ];

    // Flatten all options once
    const allStrengthOptions = flattenOptions(strengthOptions);
    const allBarrierOptions = flattenOptions(barrierOptions);
    const allNoteTriggerOptions = flattenOptions(noteTriggersOptions);
    const allNoteWarningSignOptions = flattenOptions(noteWarningSignsOptions);

    // Helper to map selected keys to full option objects
    const mapSelected = (keys, allOptions) =>
      keys.reduce((acc, key) => {
        if (plan[key]) {
          // Find full option object by value === key
          const fullOption = allOptions.find((opt) => opt.value === key);
          if (fullOption) acc.push(fullOption);
        }
        return acc;
      }, []);

    const selectedStrengths = mapSelected(strengthKeys, allStrengthOptions);
    const selectedBarriers = mapSelected(barrierKeys, allBarrierOptions);
    const selectedNoteTriggers = mapSelected(
      noteTriggerKeys,
      allNoteTriggerOptions,
    );
    const selectedNoteWarningSigns = mapSelected(
      noteWarningSignKeys,
      allNoteWarningSignOptions,
    );

    // Other Barrier text and barrier response note
    const parsedNotes = parseBarrierResponseNote(
      plan.BRResponseNote,
      !!plan.BROther,
    );
    const otherBarrierText = parsedNotes.otherBarrierText;
    const barrierNote = parsedNotes.barrierNote;

    return {
      strengths: selectedStrengths,
      barriers: selectedBarriers,
      otherBarrierText,
      noteTriggers: selectedNoteTriggers,
      noteWarningSigns: selectedNoteWarningSigns,
      barrierNote,
    };
  }
  useEffect(() => {
    if (!userId) return;

    async function loadPlan() {
      setLoading(true);
      try {
        const loaded = await loadCreatePlan(userId);
        const planForDisplay = loaded?.planForDisplay || null;
        const planForUpdate = loaded?.planForUpdate || null;

        createPlanRecordRef.current = planForUpdate;

        const {
          strengths,
          barriers,
          otherBarrierText,
          noteTriggers,
          noteWarningSigns,
          barrierNote,
        } = parseCreatePlanToSelections(planForDisplay);

        setSelectedStrengths(strengths);
        setSelectedBarriers(barriers);
        setOtherBarrierText(otherBarrierText);
        setSelectedNoteTriggers(noteTriggers);
        setSelectedNoteWarningSigns(noteWarningSigns);
        setBarrierNote(barrierNote);

        // Avoid immediately re-saving the same hydrated data.
        const fingerprint = JSON.stringify({
          strengths: (strengths || []).map((o) => o.value).sort(),
          barriers: (barriers || []).map((o) => o.value).sort(),
          noteTriggers: (noteTriggers || []).map((o) => o.value).sort(),
          noteWarningSigns: (noteWarningSigns || []).map((o) => o.value).sort(),
          otherBarrierText: (otherBarrierText || "").trim(),
          barrierNote: (barrierNote || "").trim(),
        });
        lastAutosaveFingerprintRef.current = fingerprint;
      } finally {
        didHydrateCreatePlanRef.current = true;
        setLoading(false);
      }
    }
    loadPlan();
  }, [userId]);

  useEffect(() => {
    if (!didHydrateCreatePlanRef.current) return;
    if (!hasAccess || !userId) return;

    const fingerprint = JSON.stringify({
      strengths: (selectedStrengths || []).map((o) => o.value).sort(),
      barriers: (selectedBarriers || []).map((o) => o.value).sort(),
      noteTriggers: (selectedNoteTriggers || []).map((o) => o.value).sort(),
      noteWarningSigns: (selectedNoteWarningSigns || [])
        .map((o) => o.value)
        .sort(),
      otherBarrierText: (otherBarrierText || "").trim(),
      barrierNote: (barrierNote || "").trim(),
    });

    if (fingerprint === lastAutosaveFingerprintRef.current) return;

    // Don't create a brand-new CreatePlan record unless there is something to save.
    const hasAnyData =
      (selectedStrengths?.length || 0) > 0 ||
      (selectedBarriers?.length || 0) > 0 ||
      (selectedNoteTriggers?.length || 0) > 0 ||
      (selectedNoteWarningSigns?.length || 0) > 0 ||
      (otherBarrierText || "").trim().length > 0 ||
      (barrierNote || "").trim().length > 0;
    if (!hasAnyData && !createPlanRecordRef.current?.id) return;

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    autosaveTimerRef.current = setTimeout(async () => {
      try {
        const input = mapSelectionsToCreatePlanInput({
          selectedStrengths: selectedStrengths || [],
          selectedBarriers: selectedBarriers || [],
          otherBarrierText,
          selectedNoteTriggers: selectedNoteTriggers || [],
          selectedNoteWarningSigns: selectedNoteWarningSigns || [],
          barrierNote,
          userId,
        });

        if (createPlanRecordRef.current?.id) {
          const res = await client.graphql({
            query: updateCreatePlan,
            variables: {
              input: {
                id: createPlanRecordRef.current.id,
                ...input,
              },
            },
          });
          createPlanRecordRef.current = res?.data?.updateCreatePlan || null;
        } else {
          const res = await client.graphql({
            query: createCreatePlan,
            variables: { input },
          });
          createPlanRecordRef.current = res?.data?.createCreatePlan || null;
        }

        lastAutosaveFingerprintRef.current = fingerprint;
      } catch (err) {
        // Silent autosave failure: user can still save via the existing finalize flow.
        console.error("CreatePlan autosave failed:", err);
      }
    }, 800);

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [
    hasAccess,
    userId,
    selectedStrengths,
    selectedBarriers,
    selectedNoteTriggers,
    selectedNoteWarningSigns,
    otherBarrierText,
    barrierNote,
  ]);

  function mapSelectionsToInput(
    selectedStrengths,
    selectedBarriers,
    selectedNoteTriggers,
    selectedNoteWarningSigns,
    otherBarrierText,
    barrierNote,
  ) {
    // Initialize all boolean fields to false
    const input = {
      // strengths
      PSProblemSolving: false,
      PSSelfEsteem: false,
      PSChoiceAutonomy: false,
      PSSelfCare: false,
      PSSelfAwareness: false,
      PSCreativity: false,

      CSForgiveness: false,
      CSKindness: false,
      CSGratitude: false,
      CSPatience: false,
      CSCourage: false,

      CMSCopingChoice: false,
      CMSResilience: false,
      CMSSelfRegulation: false,
      CMSOptimism: false,

      COMSupportSystem: false,
      COMSocialIntelligence: false,
      COMCommunication: false,
      COMFaithCommunity: false,
      COMAccomplishments: false,

      // barriers
      BRStableHousing: false,
      BRQualityHealthcare: false,
      BRProfessionalAccess: false,
      BRInsuranceCoverage: false,
      BRFinancialConstraints: false,
      BREducationAttainment: false,
      BRSocialStigma: false,
      BRUnsupportiveCommunity: false,
      BRNonexistentCommunity: false,
      BRCommunityResources: false,
      BRProfessionalTrust: false,
      BRHealthcareSystemTrust: false,
      BRCognitiveImpairments: false,
      BRDependentCare: false,
      BRTransportation: false,
      BRTechnologyAccess: false,
      BRLanguage: false,
      BRHealthcareProcess: false,
      BROther: false,
      BRResponseNote: "",

      // note triggers
      TGBetrayal: false,
      TGBoundaries: false,
      TGDisorder: false,
      TGPain: false,
      TGRejection: false,
      TGStress: false,
      TGMemories: false,
      TGUnjustTreatment: false,
      TGUnpleasantConversations: false,
      TGUnsafeEnvironments: false,
      TGOther: false,

      // note warning signs
      WSFlashbacks: false,
      WSIncreasedAppetite: false,
      WSDecreasedAppetite: false,
      WSIntenseEmotions: false,
      WSDisconnecting: false,
      WSTime: false,
      WSNegativeSelfTalk: false,
      WSNeglectingSelfCare: false,
      WSNightmares: false,
      WSRecklessBehavior: false,
      WSOther: false,
    };

    // Mark selected strengths true
    selectedStrengths.forEach(({ value }) => {
      if (value in input) input[value] = true;
    });

    // Mark selected barriers true
    selectedBarriers.forEach(({ value }) => {
      if (value === "BROther") {
        // If "Other" barrier text provided
        input.BROther = true;
        input.BRResponseNote = otherBarrierText || "";
      } else if (value in input) {
        input[value] = true;
      }
    });

    // Mark selected note triggers true
    selectedNoteTriggers.forEach(({ value }) => {
      if (value in input) input[value] = true;
    });

    // Mark selected warning signs true
    selectedNoteWarningSigns.forEach(({ value }) => {
      if (value in input) input[value] = true;
    });

    // You can add any other notes (like barrierNote) to the input object if needed

    return input;
  }

  // Check if user has any of these categories selected or in plans
  const showNoteDropdowns =
    selectedCategories.some((cat) => specialCategories.includes(cat)) ||
    Object.keys(allPlans).some((cat) => specialCategories.includes(cat));

  const handleBarrierChange = (selectedOptions) => {
    setSelectedBarriers(selectedOptions || []);
    if (!selectedOptions?.some((item) => item.value === "BROther")) {
      setOtherBarrierText("");
    }
  };

  function setStrengths(e) {
    const options = e.target.options;
    const selected = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selected.push(options[i].value);
      }
    }
    setSelectedStrengths(selected);
  }
  // Calculate final price given coupon
  const calculateFinalPrice = () => {
    if (!couponData) return basePrice.toFixed(2);

    let final = basePrice;
    if (couponData.type === "amount") {
      final = Math.max(0, basePrice - couponData.value);
    } else if (couponData.type === "percent") {
      final = Math.max(0, basePrice - basePrice * (couponData.value / 100));
    }
    return final.toFixed(2);
  };

  // Load user + check payment access on mount
  useEffect(() => {
    async function checkAccessAndFetch() {
      try {
        const attrs = await fetchUserAttributes();
        const uid = attrs.sub;
        if (!uid) throw new Error("User not authenticated");
        setUserId(uid);

        // Try calling AppSync directly with the Cognito idToken header
        const currentUser = await getCurrentUser().catch(() => null);
        let session = null;
        try {
          session =
            currentUser?.signInUserSession ||
            (typeof currentUser?.getSignInUserSession === "function"
              ? currentUser.getSignInUserSession()
              : null);
        } catch (e) {
          console.warn("reading signInUserSession failed:", e);
        }
        const idToken =
          session?.getIdToken?.()?.getJwtToken?.() ||
          session?.idToken?.jwtToken ||
          currentUser?.signInUserSession?.idToken?.jwtToken ||
          null;
        const userEmail = attrs.email || currentUser?.signInDetails?.loginId;
        const normalizedUserEmail = userEmail?.toLowerCase();

        console.log("checkAccessAndFetch currentUser:", currentUser);
        console.log("checkAccessAndFetch idToken present:", !!idToken);

        const res = await client.graphql({
          query: listUserPayments,
          variables: {
            filter: { or: [{ owner: { eq: uid } }, { userId: { eq: uid } }] },
            limit: 50,
          },
        });
        const paymentRecords = res?.data?.listUserPayments?.items || [];
        const paymentRecord = paymentRecords.length ? paymentRecords[0] : null;

        const now = new Date();
        const validPayments = paymentRecords.filter(
          (pr) => new Date(pr.paidUntil) > now,
        );

        if (validPayments.length > 0) {
          setHasAccess(true);
          await fetchFlaggedData(
            setSelectedCategories,
            setUserId,
            setAllPlans,
            setExistingCount,
            setSavedActionCount,
            setRemainingSlots,
            setLoading,
            setError,
          );
        } else {
          // Check if user is linked to a professional who has paid
          try {
            if (normalizedUserEmail) {
              const linksRes = await client.graphql({
                query: listUserLinks,
                variables: {
                  filter: {
                    or: [
                      { clientEmail: { eq: normalizedUserEmail } },
                      { clientEmail: { eq: userEmail } },
                    ],
                  },
                  limit: 50,
                },
              });

              let links = linksRes?.data?.listUserLinks?.items || [];
              if (links.length === 0) {
                const allLinksRes = await client.graphql({
                  query: listUserLinks,
                  variables: { limit: 500 },
                });
                const allLinks = allLinksRes?.data?.listUserLinks?.items || [];
                links = allLinks.filter(
                  (link) =>
                    (link?.clientEmail || "").toLowerCase() ===
                    normalizedUserEmail,
                );
              }
              if (links.length > 0) {
                const activeLinks = links.filter(
                  (link) =>
                    link?.clientViewable !== false &&
                    !link?.unlinkedAt &&
                    (link?.professionalId || link?.professionalEmail),
                );

                if (activeLinks.length > 0) {
                  console.log(
                    "✅ Access granted via active professional connection",
                  );
                  setHasAccess(true);
                  await fetchFlaggedData(
                    setSelectedCategories,
                    setUserId,
                    setAllPlans,
                    setExistingCount,
                    setSavedActionCount,
                    setRemainingSlots,
                    setLoading,
                    setError,
                  );
                  return;
                }

                // Check if any linked professional has active payments
                for (const link of links) {
                  const proId = link.professionalId;
                  const proEmail = link.professionalEmail;
                  const normalizedProEmail = proEmail?.toLowerCase();
                  if (!proId) continue;

                  // Check if professional belongs to an organization
                  const orgCPRes = await client.graphql({
                    query: listOrganizationCPs,
                    variables: {
                      filter: {
                        or: [
                          { OrganizationCPEmail: { eq: normalizedProEmail } },
                          { OrganizationCPEmail: { eq: proEmail } },
                        ],
                      },
                    },
                  });
                  const proCP = orgCPRes?.data?.listOrganizationCPs?.items?.[0];
                  const orgId = proCP?.OrganizationId;

                  // Use org ID if professional is in org, otherwise use professional ID
                  const paymentUserId = orgId || proId;

                  const proPaymentsRes = await client.graphql({
                    query: listUserPayments,
                    variables: {
                      filter: {
                        and: [
                          { userId: { eq: paymentUserId } },
                          { paidUntil: { gt: now.toISOString() } },
                          {
                            or: [
                              { paymentFor: { eq: "professional" } },
                              { paymentFor: { eq: "Professional" } },
                              { paymentFor: { eq: "organization" } },
                            ],
                          },
                        ],
                      },
                      limit: 50,
                    },
                  });

                  const proPayments =
                    proPaymentsRes?.data?.listUserPayments?.items || [];
                  const hasActiveProPayment = proPayments.some(
                    (p) => (p.clientsProjected || 0) > 0,
                  );

                  if (hasActiveProPayment) {
                    console.log(
                      "✅ Access granted via professional connection",
                    );
                    setHasAccess(true);
                    await fetchFlaggedData(
                      setSelectedCategories,
                      setUserId,
                      setAllPlans,
                      setExistingCount,
                      setSavedActionCount,
                      setRemainingSlots,
                      setLoading,
                      setError,
                    );
                    break;
                  }
                }
              }
            }
          } catch (linkErr) {
            console.warn("Error checking professional links:", linkErr);
          }
        }
      } catch (err) {
        console.log("Access check failed:", err);
      } finally {
        setCheckingAccess(false);
        setLoading(false);
      }
    }

    checkAccessAndFetch();
  }, []);

  // Validate coupon and handle free 100% off coupons
  const handleValidateCoupon = async () => {
    if (couponData) {
      setCouponError("You can only use one coupon at a time.");
      return;
    }

    const code = coupon.trim();
    if (!code) {
      setCouponError("Please enter a coupon code.");
      return;
    }
    setValidating(true);
    setCouponError("");
    try {
      // Ensure the user is authenticated before making a user-pool protected request
      const currentUser = await getCurrentUser().catch(() => null);
      if (!currentUser) {
        setValidating(false);
        setCouponError("You must sign in to apply coupons.");
        return;
      }

      console.log("Validating coupon:", code);

      // Debug: inspect current Cognito session / tokens to help diagnose Unauthorized
      let session = null;
      try {
        session =
          currentUser?.signInUserSession ||
          (typeof currentUser?.getSignInUserSession === "function"
            ? currentUser.getSignInUserSession()
            : null);
      } catch (e) {
        console.warn("reading signInUserSession failed:", e);
      }
      console.log("currentUser (getCurrentUser):", currentUser);
      console.log("currentSession:", session);
      try {
        console.log("idToken:", session?.getIdToken?.().getJwtToken?.());
        console.log(
          "accessToken:",
          session?.getAccessToken?.().getJwtToken?.(),
        );
      } catch (e) {
        /* ignore token read errors */
      }

      // derive id token from user session and call AppSync directly
      const idToken =
        session?.getIdToken?.()?.getJwtToken?.() ||
        session?.idToken?.jwtToken ||
        currentUser?.signInUserSession?.idToken?.jwtToken ||
        null;

      const data = await resolveCouponByCode(code);

      if (
        !data ||
        !data.isActive ||
        (data.expiresAt && new Date(data.expiresAt) < new Date()) ||
        data.timesUsed >= data.maxUses
      ) {
        throw new Error("Invalid or expired coupon");
      }

      setCouponData(data);

      // Calculate final price with coupon
      let finalPrice = basePrice;
      if (data.type === "amount") {
        finalPrice = Math.max(0.01, basePrice - data.value);
      } else if (data.type === "percent") {
        finalPrice = Math.max(0.01, basePrice - basePrice * (data.value / 100));
      }
      finalPrice = parseFloat(finalPrice.toFixed(2));

      if (
        finalPrice <= 0.01 &&
        data.type === "percent" &&
        Number(data.value) === 100
      ) {
        try {
          await grantCouponAccess(data);
        } catch (err) {
          console.error("Error applying coupon:", err);
          setCouponError(
            err?.message || "Failed to apply coupon. Please try again.",
          );
          setSuccessMsg("");
        } finally {
          setValidating(false);
        }
        return;
      }

      setCouponError("");
      setValidating(false);
      setSuccessMsg(`Coupon applied! New price: $${finalPrice}`);
    } catch (err) {
      console.error("Error during coupon validation:", err);
      setCouponData(null);
      // Surface clearer message when GraphQL returns unauthorized
      const graphQLError =
        err?.errors?.[0]?.message || (err && err.message) || "Invalid coupon";
      if (
        typeof graphQLError === "string" &&
        graphQLError.toLowerCase().includes("unauthorized")
      ) {
        setCouponError("Not authorized. Please sign in and try again.");
      } else {
        setCouponError("Invalid coupon");
      }
      setValidating(false);
      setSuccessMsg("");
    }
  };

  // fetch flagged data initially (even if user already has access)
  useEffect(() => {
    if (!hasAccess) return; // only if has access
    fetchFlaggedData(
      setSelectedCategories,
      setUserId,
      setAllPlans,
      setExistingCount,
      setSavedActionCount,
      setRemainingSlots,
      setLoading,
      setError,
    );
  }, [hasAccess]);

  // Update category plans
  const updateCategoryPlan = useCallback((key, plan) => {
    setAllPlans((prev) => ({ ...prev, [key]: plan }));
  }, []);

  // Submit selected plans/actions
  const handleSubmitAll = async () => {
    try {
      if (!userId) throw new Error("User not authenticated");

      // Fetch existing finalized plans count
      const trackPlanQuery = `
        query ListTrackPlans($filter: ModelTrackPlanFilterInput, $limit: Int) {
          listTrackPlans(filter: $filter, limit: $limit) {
            items { id }
          }
        }
      `;
      const trackPlanRes = await client.graphql({
        query: trackPlanQuery,
        variables: {
          filter: {
            or: [{ owner: { eq: userId } }, { userId: { eq: userId } }],
          },
          limit: 100,
        },
      });
      const existingItems = trackPlanRes?.data?.listTrackPlans?.items || [];
      const existingCount = existingItems.length;

      // Count new selected actions
      let newActionCount = 0;
      for (const category of selectedCategories) {
        const actions = allPlans[category] || [];
        for (const actionObj of actions) {
          if (actionObj.action) newActionCount++;
        }
      }

      if (existingCount + newActionCount > 10) {
        setSuccessMsg("");
        setErrorMsg(
          `You can select/work on up to 10 actions at one time. You have ${existingCount} finalized. Please discontinue an action before adding more.`,
        );
        setTimeout(() => setErrorMsg(""), 22000);
        return;
      }

      // Validate frequency and notes requirements
      let hasErrors = false;
      let frequencyMissing = false;
      let noteMissing = false;
      for (const category of selectedCategories) {
        const actions = allPlans[category] || [];
        for (const actionObj of actions) {
          if (!actionObj.action) continue;
          const noFrequency = actionObj.action
            .toLowerCase()
            .includes("no frequency");
          if (
            !noFrequency &&
            (!actionObj.frequency || !actionObj.frequency.trim())
          ) {
            frequencyMissing = true;
            hasErrors = true;
          }
          if (
            actionObj.needsNote &&
            (!actionObj.note || !actionObj.note.trim())
          ) {
            noteMissing = true;
            hasErrors = true;
          }
        }
      }
      if (hasErrors) {
        setSuccessMsg("");
        if (frequencyMissing && noteMissing) {
          setErrorMsg(
            "Frequency is required for all actions, and notes are required for some.",
          );
        } else if (frequencyMissing) {
          setErrorMsg("Frequency is required for all actions.");
        } else if (noteMissing) {
          setErrorMsg("Note is required for some actions.");
        }
        setTimeout(() => setErrorMsg(""), 8000);
        return;
      }

      // Total selected actions limit check
      let totalSelected = 0;
      for (const category of selectedCategories) {
        const actions = allPlans[category] || [];
        totalSelected += actions.filter((a) => a.action).length;
      }
      if (totalSelected > 10) {
        setSuccessMsg("");
        setErrorMsg("You can only select up to 10 total actions.");
        setTimeout(() => setErrorMsg(""), 8000);
        return;
      }

      setErrorMsg("");

      // Submit each valid action
      let count = 0;
      for (const category of selectedCategories) {
        const actions = allPlans[category] || [];
        for (const actionObj of actions) {
          if (!actionObj.action) continue;
          const noFrequency = actionObj.action
            .toLowerCase()
            .includes("no frequency");
          if (
            !noFrequency &&
            (!actionObj.frequency || !actionObj.frequency.trim())
          )
            continue;

          const input = {
            userId,
            CompletedDate: new Date().toISOString().slice(0, 10),
            CompletedTime: new Date().toISOString().slice(11, 19),
            ActionDomain: getDomainName(category),

            ActionQuestion: labels[category] || "",
            Action: actionObj.action.replace(/\s*\(.*?\)\s*/g, "").trim(),
            Frequency: noFrequency ? "N/A" : actionObj.frequency,
            ActionNote: actionObj.note || "N/A",
            Update1Points: null,
            Update1Note: null,
            Update1Date: null,
            Update2Points: null,
            Update2Note: null,
            Update2Date: null,
            Update3Points: null,
            Update3Note: null,
            Update3Date: null,
            Update4Points: null,
            Update4Note: null,
            Update4Date: null,
            Update5Points: null,
            Update5Note: null,
            Update5Date: null,
            Update6Points: null,
            Update6Note: null,
            Update6Date: null,
            Update7Points: null,
            Update7Note: null,
            Update7Date: null,
            Update8Points: null,
            Update8Note: null,
            Update8Date: null,
            Percentage: 0,
            Discontinued: false,
            DiscontinuedDate: null,
            Successful: false,
            SuccessfulDate: null,
          };

          await client.graphql({
            query: createTrackPlanMutation,
            variables: { input },
          });

          count++;
          try {
            // Check if a CreatePlan record already exists
            const existingCreatePlanRes = await client.graphql({
              query: listCreatePlans,
              variables: {
                filter: {
                  userId: { eq: userId },
                },
                limit: 50,
              },
            });

            const existingCreatePlans =
              existingCreatePlanRes?.data?.listCreatePlans?.items || [];

            const planToUpdate = pickActiveCreatePlan(existingCreatePlans);

            if (planToUpdate?.id) {
              // Update active (within 365 days) record
              await client.graphql({
                query: updateCreatePlan,
                variables: {
                  input: {
                    id: planToUpdate.id,
                    ...createPlanInput,
                  },
                },
              });
              console.log("CreatePlan updated successfully");
            } else {
              // No active record: create a new record
              await client.graphql({
                query: createCreatePlan,
                variables: { input: createPlanInput },
              });
              console.log("CreatePlan created successfully");
            }
          } catch (err) {
            console.error("Failed to save CreatePlan:", err);
            // optionally set error state here
          }
        }
      }

      if (count === 0) {
        setSuccessMsg("");
        setErrorMsg("No valid actions were selected.");
        setTimeout(() => setErrorMsg(""), 8000);
      } else {
        setErrorMsg("");
        setSuccessMsg(`Finalized ${count} action(s) successfully!`);
        setTimeout(() => setSuccessMsg(""), 8000);
        setAllPlans({});
        setResetFlag((prev) => !prev);

        // Refresh existing count
        const refreshQuery = `
          query ListTrackPlans {
            listTrackPlans(filter: {
              or: [{ owner: { eq: "${userId}" } }, { userId: { eq: "${userId}" } }]
            }, limit: 100) {
              items { id }
            }
          }
        `;
        const refreshRes = await client.graphql({ query: refreshQuery });
        const refreshedItems = refreshRes?.data?.listTrackPlans?.items || [];
        setExistingCount(refreshedItems.length);
        setSavedActionCount(refreshedItems.length);
      }
      try {
        const userAttrs = await fetchUserAttributes();

        const userEmail = userAttrs.email;
        // 1. Query UserLink where clientEmail matches
        const userLinksData = await client.graphql({
          query: listUserLinks,
          variables: {
            filter: {
              clientEmail: { eq: userEmail },
            },
          },
        });

        const userLinks = userLinksData.data.listUserLinks.items;

        if (!userLinks.length) {
          console.log("No linked professionals found for this client.");
          return;
        }

        // 2. Extract all professional emails
        const linkedProfessionals = userLinks.map((link) => ({
          email: link.professionalEmail,
          userId: link.professionalId || null,
        }));

        // 3. Loop through each professional to send a notification
        for (const professional of linkedProfessionals) {
          const email = professional.email;
          try {
            // Update original notification status

            const userAttrs = await fetchUserAttributes();
            const responderId = userAttrs.sub;
            const responderFirstName = userAttrs["given_name"] || "";
            const responderLastName = userAttrs["family_name"] || "";
            const responderEmail = userAttrs.email;
            let organizationId = null;
            try {
              const orgCPRes = await client.graphql({
                query: listOrganizationCPs,
                variables: {
                  filter: {
                    OrganizationCPEmail: {
                      eq: String(email || "")
                        .trim()
                        .toLowerCase(),
                    },
                  },
                  limit: 1,
                },
              });
              organizationId =
                orgCPRes?.data?.listOrganizationCPs?.items?.[0]
                  ?.OrganizationId || null;
            } catch (e) {
              organizationId = null;
            }
            // Create RecommendationBack notification
            const newNote = {
              RecommendationNote: `${responderFirstName} ${responderLastName} has an plan ready for you to reveiw`,

              SenderId: responderId,
              SenderFirstName: responderFirstName,
              SenderLastName: responderLastName,
              SenderEmail: String(userEmail || "")
                .trim()
                .toLowerCase(),
              TargetEmail: String(email || "")
                .trim()
                .toLowerCase(),
              TargetUserId: professional.userId,
              NotificationType: "ReviewReady",
              Status: "pending",
              StatusDate: new Date().toISOString().split("T")[0],
              ...(organizationId ? { organizationId } : {}),
            };

            await client.graphql({
              query: createNotifications,
              variables: { input: newNote },
            });
          } catch (err) {
            console.error("Error responding to recommendation:", err);
          }
          console.log(`Notification sent to ${email}`);
        }
      } catch (error) {
        console.error("Error getting users professionals:", error);
      }
    } catch (err) {
      console.error("Submission error:", err);
      setSuccessMsg("");
      setErrorMsg(
        "Submission failed: " + (err?.errors?.[0]?.message || err?.message),
      );
      setTimeout(() => setErrorMsg(""), 4000);
    }
  };
  const createPlanInput = mapSelectionsToCreatePlanInput({
    selectedStrengths,
    selectedBarriers,
    otherBarrierText,
    selectedNoteTriggers,
    selectedNoteWarningSigns,
    barrierNote,
    userId,
  });

  function mapSelectionsToCreatePlanInput({
    selectedStrengths,
    selectedBarriers,
    otherBarrierText,
    selectedNoteTriggers,
    selectedNoteWarningSigns,
    barrierNote,
    userId,
  }) {
    // Start with all booleans false, and empty string notes
    const input = {
      userId,
      PSProblemSolving: false,
      PSSelfEsteem: false,
      PSChoiceAutonomy: false,
      PSSelfCare: false,
      PSSelfAwareness: false,
      PSCreativity: false,

      CSForgiveness: false,
      CSKindness: false,
      CSGratitude: false,
      CSPatience: false,
      CSCourage: false,

      CMSCopingChoice: false,
      CMSResilience: false,
      CMSSelfRegulation: false,
      CMSOptimism: false,

      COMSupportSystem: false,
      COMSocialIntelligence: false,
      COMCommunication: false,
      COMFaithCommunity: false,
      COMAccomplishments: false,

      BRStableHousing: false,
      BRQualityHealthcare: false,
      BRProfessionalAccess: false,
      BRInsuranceCoverage: false,
      BRFinancialConstraints: false,
      BREducationAttainment: false,
      BRSocialStigma: false,
      BRUnsupportiveCommunity: false,
      BRNonexistentCommunity: false,
      BRCommunityResources: false,
      BRProfessionalTrust: false,
      BRHealthcareSystemTrust: false,
      BRCognitiveImpairments: false,
      BRDependentCare: false,
      BRTransportation: false,
      BRTechnologyAccess: false,
      BRLanguage: false,
      BRHealthcareProcess: false,
      BROther: false,
      BRResponseNote: encodeBarrierResponseNote(otherBarrierText, barrierNote),

      TGBetrayal: false,
      TGBoundaries: false,
      TGDisorder: false,
      TGPain: false,
      TGRejection: false,
      TGStress: false,
      TGMemories: false,
      TGUnjustTreatment: false,
      TGUnpleasantConversations: false,
      TGUnsafeEnvironments: false,
      TGOther: false,

      WSFlashbacks: false,
      WSIncreasedAppetite: false,
      WSDecreasedAppetite: false,
      WSIntenseEmotions: false,
      WSDisconnecting: false,
      WSTime: false,
      WSNegativeSelfTalk: false,
      WSNeglectingSelfCare: false,
      WSNightmares: false,
      WSRecklessBehavior: false,
      WSOther: false,
    };

    // Mark selected strengths true
    selectedStrengths.forEach(({ value }) => {
      if (value in input) input[value] = true;
    });

    // Mark selected barriers true
    selectedBarriers.forEach(({ value }) => {
      if (value === "BROther") {
        input.BROther = true;
      } else if (value in input) {
        input[value] = true;
      }
    });

    // Mark note triggers true
    selectedNoteTriggers.forEach(({ value }) => {
      if (value in input) input[value] = true;
    });

    // Mark warning signs true
    selectedNoteWarningSigns.forEach(({ value }) => {
      if (value in input) input[value] = true;
    });

    return input;
  }

  if (loading)
    return <p style={{ padding: 20 }}>Loading your health plan...</p>;
  if (checkingAccess)
    return <p style={{ padding: 20 }}>Checking your subscription...</p>;

  const totalSelectedCount = Object.values(allPlans)
    .flat()
    .filter((a) => a?.action).length;

  if (!hasAccess) {
    const finalPrice = calculateFinalPrice();

    return (
      <div
        style={{
          width: 350,
          alignItems: "center",
          minWidth: 300,
          backgroundColor: "white",
          padding: 16,
          borderRadius: 12,
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          color: "#333",
        }}
      >
        <img
          src={(process.env.PUBLIC_URL || "") + "/Icon-CP-Blue.png"}
          alt="Logo"
          style={{
            width: "100%",
            maxWidth: 200,
            height: "auto",
            display: "block",
            margin: "0 auto 30px auto",
          }}
        />
        <h2 style={{ marginBottom: 16 }}>Access Your Health Plan</h2>
        <p style={{ padding: "0 12px" }}>
          This feature is <strong>$10</strong> per year. The FREETRIAL coupon
          gives you 14 days of access before payment is required again. During
          this time you will have unlimited access to create and edit your plan,
          track your progress, make notes, and view and download your reports.
          If you have a coupon add it below.
        </p>

        <div style={{ marginTop: 16 }}>
          <input
            placeholder="Enter Coupon Code"
            value={coupon}
            onChange={(e) => setCoupon(e.target.value)}
            style={{ width: "100%", padding: 8 }}
            disabled={validating}
          />
          <button
            onClick={handleValidateCoupon}
            disabled={validating || !coupon.trim()}
            style={{ marginTop: 8, width: "100%", padding: 10 }}
          >
            {validating ? "Checking..." : "Apply Coupon"}
          </button>
          {couponData && (
            <button
              onClick={() => {
                setCouponData(null);
                setCoupon("");
                setCouponError("");
                setSuccessMsg("");
              }}
              style={{ marginTop: 8 }}
            >
              Remove Coupon
            </button>
          )}

          {couponError && <p style={{ color: "red" }}>{couponError}</p>}
          {successMsg && <p style={{ color: "green" }}>{successMsg}</p>}
          {couponData && !successMsg && (
            <p style={{ color: "green", marginTop: 8 }}>
              Coupon applied:{" "}
              {couponData.type === "percent"
                ? `${couponData.value}% off`
                : `$${couponData.value} off`}
            </p>
          )}
        </div>

        <div style={{ marginTop: 24 }}>
          <p>
            <strong>Total:</strong> ${finalPrice}
          </p>

          <PayPalScriptProvider
            options={{
              "client-id": PAYPAL_CLIENT_ID,
            }}
          >
            <PayPalButtons
              style={{ layout: "vertical" }}
              forceReRender={[finalPrice]}
              createOrder={(data, actions) =>
                actions.order.create({
                  purchase_units: [
                    {
                      amount: { value: finalPrice },
                    },
                  ],
                })
              }
              onApprove={async (data, actions) => {
                const details = await actions.order.capture();
                console.log("Payment successful:", details);

                try {
                  await client.graphql({
                    query: updateUserPayments,
                    variables: {
                      input: {
                        id: userId,
                        userId,
                        paidUntil: new Date(
                          new Date().setFullYear(new Date().getFullYear() + 1),
                        ).toISOString(),
                      },
                    },
                  });

                  if (couponData) {
                    await client.graphql({
                      query: updateCoupon,
                      variables: {
                        input: {
                          id: couponData.id,
                          timesUsed: (couponData.timesUsed || 0) + 1,
                          _version: couponData._version,
                        },
                      },
                    });
                  }

                  setHasAccess(true);
                  await fetchFlaggedData(
                    setSelectedCategories,
                    setUserId,
                    setAllPlans,
                    setExistingCount,
                    setSavedActionCount,
                    setRemainingSlots,
                    setLoading,
                    setError,
                  );
                } catch (err) {
                  console.error(
                    "Error updating payment after PayPal success:",
                    err,
                  );
                }
              }}
              onError={(err) => {
                console.error("PayPal error:", err);
                alert("Payment failed. Please try again.");
              }}
            />
          </PayPalScriptProvider>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          width: 350,
          alignItems: "center",
          minWidth: 300,
          backgroundColor: "white",
          padding: 16,
          borderRadius: 12,
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          color: "#333",
        }}
      >
        <img
          src={(process.env.PUBLIC_URL || "") + "/Icon-CP-Blue.png"}
          alt="Logo"
          style={{
            width: "100%",
            maxWidth: 200,
            height: "auto",
            display: "block",
            margin: "0px auto 30px auto",
          }}
        />
        <h1 style={{ fontSize: 28, marginBottom: 8, marginTop: 0 }}>
          Create Health Plan
        </h1>
        <p style={{ fontSize: 16, marginBottom: 16, padding: "0 12px" }}>
          The areas below are shown because your scores were outside of a
          healthy range. Click actions to select and set your personal plan. You
          can select / work on 10 actions at one time.
        </p>
        {hasExistingPlans && (
          <div style={{ padding: "0 12px" }}>
            <div
              style={{
                backgroundColor: "#fffbe6",
                border: "1px solid #ffd700",
                padding: "12px 16px",
                borderRadius: 8,
                marginBottom: 16,
                color: "#997c00",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              You have actions in progress. To track or discontinue an action
              visit the <strong>Track Progress</strong> page.
            </div>
          </div>
        )}
        <h1 style={{ marginBottom: 16, fontSize: 24, fontWeight: "bold" }}>
          Strengths
        </h1>
        <div style={{ padding: "0 12px" }}>
          <Select
            options={strengthOptions}
            value={selectedStrengths}
            onChange={setSelectedStrengths}
            isMulti
            closeMenuOnSelect={false}
            placeholder="Select your strengths..."
          />
        </div>
        <h1 style={{ marginBottom: 16, fontSize: 24, fontWeight: "bold" }}>
          Barriers
        </h1>
        <div style={{ padding: "0 12px" }}>
          <Select
            options={barrierOptions}
            isMulti
            closeMenuOnSelect={false}
            placeholder="Select barriers..."
            value={selectedBarriers}
            onChange={handleBarrierChange}
          />
        </div>
        {isOtherBarrierSelected && (
          <div style={{ padding: "0 12px" }}>
            <textarea
              placeholder="Please specify other barrier..."
              value={otherBarrierText}
              onChange={(e) => setOtherBarrierText(e.target.value)}
              style={{
                marginTop: 8,
                width: "100%",
                minHeight: 70,
                padding: 8,
                borderRadius: 6,
                border: "1px solid #ccc",
                fontSize: 14,
                resize: "vertical",
              }}
            />
          </div>
        )}

        <div style={{ padding: "0 12px" }}>
          <textarea
            placeholder="What can be/is being done to remove barriers?"
            value={barrierNote}
            onChange={(e) => setBarrierNote(e.target.value)}
            style={{
              marginTop: 16,
              width: "100%",
              minHeight: 90,
              padding: 10,
              borderRadius: 6,
              border: "1px solid #ccc",
              fontSize: 15,
              resize: "vertical",
            }}
          />
        </div>

        {showNoteDropdowns && (
          <>
            <h2>Note Triggers</h2>
            <div style={{ padding: "0 12px" }}>
              <Select
                options={noteTriggersOptions}
                isMulti
                closeMenuOnSelect={false}
                placeholder="Select note triggers..."
                value={selectedNoteTriggers}
                onChange={setSelectedNoteTriggers}
              />
            </div>

            <h2 style={{ marginTop: 24 }}>Note Warning Signs</h2>
            <div style={{ padding: "0 12px" }}>
              <Select
                options={noteWarningSignsOptions}
                isMulti
                closeMenuOnSelect={false}
                placeholder="Select warning signs..."
                value={selectedNoteWarningSigns}
                onChange={setSelectedNoteWarningSigns}
              />
            </div>
          </>
        )}

        <div style={{ padding: "0 12px", marginTop: 16 }}>
          <button
            onClick={saveCreatePlanSelections}
            disabled={savingSelections}
            style={{
              width: "100%",
              backgroundColor: "#0067B3",
              color: "white",
              border: "none",
              padding: "12px 14px",
              borderRadius: 10,
              fontWeight: "bold",
              cursor: savingSelections ? "not-allowed" : "pointer",
              opacity: savingSelections ? 0.7 : 1,
            }}
          >
            {savingSelections ? "Saving..." : "Save Choices"}
          </button>

          {selectionsSaveError ? (
            <div style={{ color: "#B00000", marginTop: 8 }}>
              Save failed: {selectionsSaveError}
            </div>
          ) : null}
          {selectionsSavedAt ? (
            <div style={{ color: "#0067B3", marginTop: 8, fontWeight: 600 }}>
              Saved
            </div>
          ) : null}
        </div>
        {error ? (
          <div style={{ padding: "0 12px", color: "#8A4B00", marginTop: 12 }}>
            {error}
          </div>
        ) : null}
        <div style={{ padding: "0 12px" }}>
          {selectedCategories.map((key) => (
            <CopingPlanCard
              key={key}
              categoryKey={key}
              actions={Actions[key]}
              onPlanUpdate={updateCategoryPlan}
              maxAllowed={remainingSlots} // <-- pass remainingSlots here
              totalSelectedCount={totalSelectedCount}
              resetFlag={resetFlag}
              existingCount={existingCount}
            />
          ))}
        </div>

        <div style={{ padding: "0 12px" }}>
          <button
            onClick={handleSubmitAll}
            style={{
              marginTop: 16,
              marginBottom: 16,
              width: "100%",
              backgroundColor: "#0067B3",
              color: "white",
              fontWeight: "700",
              fontSize: 16,
              borderRadius: 8,
              padding: "12px 0",
              border: "none",
              cursor: "pointer",
              userSelect: "none",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            }}
          >
            Finalize Chosen Actions
          </button>
        </div>
        {errorMsg && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 16px",
              backgroundColor: "#ffe6e6",
              color: "#b00000",
              borderRadius: 8,
              textAlign: "center",
              fontWeight: "bold",
              border: "1px solid #b00000",
            }}
          >
            {errorMsg}
          </div>
        )}
        {successMsg && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 16px",
              backgroundColor: "#e0f0ff",
              color: "#0067B3",
              borderRadius: 8,
              textAlign: "center",
              fontWeight: "bold",
              border: "1px solid #0067B3",
            }}
          >
            {successMsg}
          </div>
        )}
      </div>
    </div>
  );
}
